/* eslint-disable no-unused-vars */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Container,
  Stack,
  Title,
  Text,
  Grid,
  Card,
  Group,
  Badge,
  Box,
  LoadingOverlay,
  Flex,
  Button,
  Paper,
  Radio,
  Textarea,
  Alert,
  Tabs,
  Avatar,
  Divider,
  List,
  Tooltip,
  ScrollArea,
  Accordion,
  TextInput,
  CopyButton,
  ActionIcon,
  Select
} from '@mantine/core';
import {
  IconCalendar,
  IconClock,
  IconCheck,
  IconAlertCircle,
  IconUser,
  IconDeviceFloppy,
  IconSend,
  IconDownload,
  IconTrash,
  IconFile,
  IconUsers,
  IconCopy,
  IconQrcode,
  IconArrowRight,
  IconArrowDown
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getAssessmentAvailabilityStatus } from 'utils/assessmentStatus';

dayjs.extend(utc);
dayjs.extend(timezone);
import { assessmentScheduleService } from 'services/lia/assessmentScheduleService';
import NotFoundPage from 'pages/NotFound';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
import QRCode from 'qrcode';
import { encodeShareToken } from 'utils/shareToken';

interface RangeItem {
  trAssessmentQuestionRangeItemID: string;
  label: string;
  value: string;
  sequence: number;
}

interface Question {
  trAssessmentQuestionID: string;
  title: string;
  description?: string;
  type: string;
  moduleId?: string;
  respondentType?: string;
  rangeItems?: RangeItem[];
  typeLabel?: string; // Role grouping for external questions (e.g., HQ, School)
}

interface Module {
  moduleID: string;
  moduleName?: string;
  trAssessmentModuleID?: string;
  questions?: Question[];
}

interface Participant {
  binusianId: string;
  binusianID?: string;
  binusianName?: string;
  name: string;
  email?: string;
  type?: string;
  division?: string;
  trAssessmentParticipantID?: string;
  listModule?: string[]; // Array of module IDs assigned to this participant
}

interface Evidence {
  evidenceUrl: string;
  fileName: string;
  fileSize: string;
  uploadDate: string;
}

const PublicAssessmentDetailPage: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const encryptedEmail = searchParams.get('e') || '';

  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [isGeneratingQr, setIsGeneratingQr] = useState(false);

  const [responses, setResponses] = useState<
    Record<string, Record<string, Record<string, string>>>
  >({});

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [activeModule, setActiveModule] = useState<string | null>(null);
  const [activeParticipant, setActiveParticipant] = useState<string | null>(null);
  const [completedModules, setCompletedModules] = useState<string[]>([]);
  const [draftSaved, setDraftSaved] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  // External role (typeLabel) selection for sharing
  const [selectedExternalTypeLabel, setSelectedExternalTypeLabel] = useState<string | null>(null);
  const [availableExternalTypeLabels, setAvailableExternalTypeLabels] = useState<string[]>([]);

  const [moduleEvidenceFiles, setModuleEvidenceFiles] = useState<Record<string, File[]>>({});
  const [uploadingModule, setUploadingModule] = useState<string | null>(null);
  const questionSectionRef = useRef<HTMLDivElement>(null);
  const [questionCompletionStatus, setQuestionCompletionStatus] = useState<
    Record<string, Record<string, Record<string, boolean>>>
  >({});

  const {
    data: assessment,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['publicAssessmentDetail', encryptedEmail],
    queryFn: () =>
      assessmentScheduleService.getPublicDetailAssessmentScheduleByEmail(encryptedEmail),
    enabled: !!encryptedEmail,
    refetchOnWindowFocus: false
  });

  const owner = assessment?.owner || {};

  const shareLink = useMemo(() => {
    const base = assessment?.linkExternal || '';
    if (!base) return '';

    const be = owner?.email || '';
    // We include token when owner is Participant (internal sharing context) and we have an email
    if (!be || owner?.type !== 'Participant') return base;

    // Resolve the participant's trAssessmentParticipantID by matching owner email
    const matchedParticipant = (assessment?.participants || []).find(
      (p: Participant) => (p.email || '').toLowerCase() === be.toLowerCase()
    );
    const pid = matchedParticipant?.trAssessmentParticipantID || undefined;

    // For both Individu & Team we want role-specific links if a role is chosen
    const token = encodeShareToken({ be, typeLabel: selectedExternalTypeLabel || undefined, pid });
    try {
      const url = new URL(base, window.location.origin);
      url.searchParams.set('tk', token);
      return url.toString();
    } catch {
      const hasQuery = base.includes('?');
      const separator = hasQuery ? '&' : '?';
      return `${base}${separator}tk=${encodeURIComponent(token)}`;
    }
  }, [
    assessment?.linkExternal,
    assessment?.participants,
    owner?.type,
    owner?.email,
    selectedExternalTypeLabel
  ]);

  // Derive available external type labels from questions
  useEffect(() => {
    if (!assessment?.modules) return;
    const labels = new Set<string>();
    assessment.modules.forEach((m: any) => {
      (m.questions || []).forEach((q: Question) => {
        const isExternal = q.respondentType?.toLowerCase() === 'external';
        if (isExternal && q.typeLabel) {
          labels.add(q.typeLabel);
        }
      });
    });
    const arr = Array.from(labels);
    setAvailableExternalTypeLabels(arr);
    if (arr.length === 1) {
      setSelectedExternalTypeLabel(arr[0]);
    }
  }, [assessment?.modules]);

  // Enhanced validator functions
  const validateParticipantEvidence = (assessment: any, activeModule: string | null): boolean => {
    if (owner?.type !== 'Participant' || assessment?.type?.toLowerCase() !== 'product') {
      return true;
    }

    if (!activeModule) return false;

    const hasEvidence =
      assessment.moduleEvidence &&
      assessment.moduleEvidence[activeModule] &&
      Array.isArray(assessment.moduleEvidence[activeModule]) &&
      assessment.moduleEvidence[activeModule].length > 0;

    return hasEvidence;
  };

  const getQuestionStatus = useCallback(
    (moduleId: string, questionId: string, participantId?: string): boolean => {
      if (assessment?.typeAssessment === 'Individu' && participantId) {
        const answer = responses[moduleId]?.[participantId]?.[questionId];
        return !!(answer && answer.trim() !== '');
      } else {
        const answer = responses[moduleId]?.team?.[questionId];
        return !!(answer && answer.trim() !== '');
      }
    },
    [responses, assessment]
  );

  const validateIndividualAssessmentCompletion = (assessment: any, responses: any): boolean => {
    if (owner?.type !== 'Respondent' || assessment?.typeAssessment !== 'Individu') {
      return true;
    }

    if (!assessment.owner?.participants || !Array.isArray(assessment.owner.participants)) {
      return false;
    }

    const participants = assessment.owner.participants;

    return participants.every((participant: Participant) => {
      const participantId = participant.binusianId || participant.binusianID;

      if (!participantId) return false;

      return assessment.modules.every((module: Module) => {
        const moduleQuestions = module.questions || [];
        const ownerRespondentType = owner?.respondentType || '';

        const relevantQuestions = moduleQuestions.filter((q: Question) =>
          owner?.isSelfAssess
            ? q.respondentType === 'Self' || q.respondentType === ownerRespondentType
            : q.respondentType === ownerRespondentType
        );

        if (relevantQuestions.length === 0) return true;

        const participantResponses = responses[module.moduleID]?.[participantId] || {};

        return relevantQuestions.every((question: Question) => {
          const answer = participantResponses[question.trAssessmentQuestionID];
          return answer && answer.trim() !== '';
        });
      });
    });
  };

  const getParticipantAllowedModules = useCallback(() => {
    if (!owner || owner.type !== 'Participant') {
      return assessment?.modules || [];
    }

    // Debug logging
    // eslint-disable-next-line no-console
    console.log('🔍 getParticipantAllowedModules - Owner:', {
      type: owner.type,
      binusianID: owner.binusianID,
      binusianId: owner.binusianId,
      email: owner.email
    });
    // eslint-disable-next-line no-console
    console.log(
      '🔍 getParticipantAllowedModules - Available participants:',
      assessment?.participants
    );

    // Try to find current participant by binusianID or email
    const currentParticipant = assessment?.participants?.find(
      (p: any) =>
        p.binusianID === owner.binusianID ||
        p.binusianId === owner.binusianID ||
        p.binusianID === owner.binusianId ||
        p.email === owner.email
    );

    // eslint-disable-next-line no-console
    console.log('🔍 getParticipantAllowedModules - Found participant:', currentParticipant);
    // eslint-disable-next-line no-console
    console.log('🔍 getParticipantAllowedModules - listModule:', currentParticipant?.listModule);

    // If no participant found or no listModule, return all modules
    if (!currentParticipant?.listModule || currentParticipant.listModule.length === 0) {
      // eslint-disable-next-line no-console
      console.log('⚠️ getParticipantAllowedModules - No listModule found, returning all modules');
      return assessment?.modules || [];
    }

    // Filter modules based on listModule array
    const filteredModules = assessment.modules.filter((module: any) =>
      currentParticipant.listModule.includes(module.moduleID)
    );

    // eslint-disable-next-line no-console
    console.log('✅ getParticipantAllowedModules - Filtered modules:', {
      total: assessment?.modules?.length,
      filtered: filteredModules.length,
      filteredModuleIds: filteredModules.map((m: any) => m.moduleID)
    });

    return filteredModules;
  }, [owner, assessment]);

  // Enhanced progress tracking with validation
  const getDetailedProgressData = useMemo(() => {
    if (!assessment)
      return {
        answeredQuestions: 0,
        totalQuestions: 0,
        progressPercentage: 0,
        moduleProgress: {},
        participantProgress: {},
        evidenceProgress: {},
        validationErrors: []
      };

    const validationErrors: string[] = [];
    const moduleProgress: {
      [moduleId: string]: { completed: number; total: number; percentage: number };
    } = {};
    const participantProgress: {
      [participantId: string]: { completed: number; total: number; percentage: number };
    } = {};
    const evidenceProgress: { [moduleId: string]: boolean } = {};

    let totalQuestions = 0;
    let answeredQuestions = 0;

    // Filter modules for Participant based on listModule
    const modulesToProcess =
      owner?.type === 'Participant' ? getParticipantAllowedModules() : assessment.modules;

    modulesToProcess.forEach((module: Module) => {
      const moduleQuestions = module.questions || [];
      const ownerRespondentType = owner?.respondentType || '';

      const relevantQuestions = moduleQuestions.filter((q: Question) =>
        owner?.isSelfAssess
          ? q.respondentType === 'Self' || q.respondentType === ownerRespondentType
          : q.respondentType === ownerRespondentType
      );

      totalQuestions += relevantQuestions.length;

      // Evidence progress for participants
      if (owner?.type === 'Participant' && assessment?.type?.toLowerCase() === 'product') {
        const hasEvidence = validateParticipantEvidence(assessment, module.moduleID);
        evidenceProgress[module.moduleID] = hasEvidence;

        if (!hasEvidence) {
          validationErrors.push(
            `Module "${module.moduleName || module.moduleID}" requires evidence upload`
          );
        }
      }

      if (assessment.typeAssessment === 'Individu' && assessment.owner.participants) {
        assessment.owner.participants.forEach((participant: Participant) => {
          const participantId = participant.binusianId || participant.binusianID;

          // Skip if participantId is undefined
          if (!participantId) return;

          const participantResponses = responses[module.moduleID]?.[participantId] || {};

          const answeredInModule = relevantQuestions.filter((q: Question) =>
            participantResponses[q.trAssessmentQuestionID]?.trim()
          ).length;

          if (!participantProgress[participantId]) {
            participantProgress[participantId] = { completed: 0, total: 0, percentage: 0 };
          }

          participantProgress[participantId].completed += answeredInModule;
          participantProgress[participantId].total += relevantQuestions.length;
          answeredQuestions += answeredInModule;
        });
      } else {
        const teamResponses = responses[module.moduleID]?.team || {};
        const answeredInModule = relevantQuestions.filter((q: Question) =>
          teamResponses[q.trAssessmentQuestionID]?.trim()
        ).length;

        moduleProgress[module.moduleID] = {
          completed: answeredInModule,
          total: relevantQuestions.length,
          percentage:
            relevantQuestions.length > 0
              ? Math.round((answeredInModule / relevantQuestions.length) * 100)
              : 100
        };

        answeredQuestions += answeredInModule;
      }
    });

    Object.keys(participantProgress).forEach((participantId) => {
      const progress = participantProgress[participantId];
      progress.percentage =
        progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 100;
    });

    const progressPercentage =
      totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0;

    return {
      answeredQuestions,
      totalQuestions,
      progressPercentage,
      moduleProgress,
      participantProgress,
      evidenceProgress,
      validationErrors
    };
  }, [responses, assessment, owner, getParticipantAllowedModules]);

  const generateQRCode = async (url: string): Promise<string> => {
    try {
      setIsGeneratingQr(true);
      const qrDataUrl = await QRCode.toDataURL(url, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      });
      return qrDataUrl;
    } catch {
      throw new Error('Failed to generate QR code');
    } finally {
      setIsGeneratingQr(false);
    }
  };

  const downloadQRCode = async () => {
    if (!shareLink) return;

    try {
      let qrDataUrl = qrCodeDataUrl;

      if (!qrDataUrl) {
        qrDataUrl = await generateQRCode(shareLink);
        setQrCodeDataUrl(qrDataUrl);
      }

      // Create download link
      const link = document.createElement('a');
      link.href = qrDataUrl;
      link.download = `assessment-qr-${assessment.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showSuccessNotification({
        message: 'QR code downloaded successfully'
      });
    } catch {
      showErrorNotification({
        message: 'Failed to download QR code'
      });
    }
  };

  useEffect(() => {
    if (!assessment?.modules) return;

    const newStatus: Record<string, Record<string, Record<string, boolean>>> = {};

    // Filter modules for Participant based on listModule
    const modulesToProcess =
      owner?.type === 'Participant' ? getParticipantAllowedModules() : assessment.modules;

    modulesToProcess.forEach((module: Module) => {
      newStatus[module.moduleID] = {};

      const ownerRespondentType = owner?.respondentType || '';

      let relevantQuestions: Question[] = [];

      if (owner?.type === 'Participant') {
        relevantQuestions = (module.questions || []).filter(
          (q: Question) => q.respondentType === 'Self'
        );
      } else {
        relevantQuestions = (module.questions || []).filter((q: Question) =>
          owner?.isSelfAssess
            ? q.respondentType === 'Self' || q.respondentType === ownerRespondentType
            : q.respondentType === ownerRespondentType
        );
      }

      if (
        assessment.typeAssessment === 'Individu' &&
        assessment.owner.participants &&
        owner?.type === 'Respondent'
      ) {
        assessment.owner.participants.forEach((participant: Participant) => {
          const participantId = participant.binusianId || participant.binusianID;
          if (participantId) {
            newStatus[module.moduleID][participantId] = {};

            relevantQuestions.forEach((question: Question) => {
              newStatus[module.moduleID][participantId][question.trAssessmentQuestionID] =
                getQuestionStatus(module.moduleID, question.trAssessmentQuestionID, participantId);
            });
          }
        });
      } else {
        newStatus[module.moduleID]['team'] = {};
        relevantQuestions.forEach((question: Question) => {
          newStatus[module.moduleID]['team'][question.trAssessmentQuestionID] = getQuestionStatus(
            module.moduleID,
            question.trAssessmentQuestionID
          );
        });
      }
    });

    setQuestionCompletionStatus(newStatus);
  }, [responses, assessment, owner, getQuestionStatus, getParticipantAllowedModules]);

  useEffect(() => {
    if (owner?.type === 'Participant' && shareLink) {
      // Regenerate every time shareLink changes (role changes, etc.)
      generateQRCode(shareLink)
        .then(setQrCodeDataUrl)
        .catch((error) => {
          showErrorNotification({
            message: 'Failed to generate QR code ' + error.message
          });
        });
    }
  }, [shareLink, owner?.type]);

  const ValidationFeedback: React.FC = () => {
    if (assessment.isAnswer) {
      return null;
    }

    let tree: Array<{
      participant: Participant;
      modules: Array<{
        module: Module;
        unansweredQuestions: Question[];
        totalQuestions: number;
        completed: number;
      }>;
    }> = [];

    if (
      assessment.typeAssessment === 'Individu' &&
      assessment.owner.participants &&
      owner?.type === 'Respondent'
    ) {
      tree = assessment.owner.participants.map((participant: Participant) => {
        const participantId = participant.binusianId || participant.binusianID;
        const modules = (assessment.modules || [])
          .map((module: Module) => {
            const ownerRespondentType = owner?.respondentType || '';
            const relevantQuestions = (module.questions || []).filter((q: Question) => {
              return owner?.isSelfAssess
                ? q.respondentType === 'Self' || q.respondentType === ownerRespondentType
                : q.respondentType === ownerRespondentType;
            });
            // Only show modules with relevant questions
            if (relevantQuestions.length === 0) return null;
            const unansweredQuestions = relevantQuestions.filter((question: Question) => {
              const isAnswered =
                questionCompletionStatus[module.moduleID]?.[participantId ?? '']?.[
                  question.trAssessmentQuestionID
                ];
              return !isAnswered;
            });
            const totalQuestions = relevantQuestions.length;
            const completed = totalQuestions - unansweredQuestions.length;
            return {
              module,
              unansweredQuestions,
              totalQuestions,
              completed
            };
          })
          .filter(
            (
              x: unknown
            ): x is {
              module: Module;
              unansweredQuestions: Question[];
              totalQuestions: number;
              completed: number;
            } => Boolean(x)
          );
        return {
          participant,
          modules
        };
      });
    }

    let teamModules: Array<{
      module: Module;
      unansweredQuestions: Question[];
      totalQuestions: number;
      completed: number;
    }> = [];

    if (assessment.typeAssessment !== 'Individu') {
      teamModules = (assessment.modules || [])
        .map((module: Module) => {
          const ownerRespondentType = owner?.respondentType || '';
          const moduleQs = module.questions || [];
          let relevantQuestions: Question[] = [];
          if (owner?.type === 'Participant') {
            relevantQuestions = moduleQs.filter((q: Question) => q.respondentType === 'Self');
          } else {
            relevantQuestions = moduleQs.filter((q: Question) =>
              owner?.isSelfAssess
                ? q.respondentType === 'Self' || q.respondentType === ownerRespondentType
                : q.respondentType === ownerRespondentType
            );
          }
          if (relevantQuestions.length === 0) return null;

          const unansweredQuestions = relevantQuestions.filter((question: Question) => {
            const isAnswered =
              questionCompletionStatus[module.moduleID]?.team?.[question.trAssessmentQuestionID];
            return !isAnswered;
          });
          const totalQuestions = relevantQuestions.length;
          const completed = totalQuestions - unansweredQuestions.length;
          return { module, unansweredQuestions, totalQuestions, completed };
        })
        .filter(
          (
            x: unknown
          ): x is {
            module: Module;
            unansweredQuestions: Question[];
            totalQuestions: number;
            completed: number;
          } => Boolean(x)
        );
    }

    let participantModules: Array<{
      module: Module;
      unansweredQuestions: Question[];
      totalQuestions: number;
      completed: number;
      needsEvidence?: boolean;
    }> = [];

    if (owner?.type === 'Participant') {
      // Filter modules based on participant's listModule
      const allowedModules = getParticipantAllowedModules();
      participantModules = (allowedModules || [])
        .map((module: Module) => {
          const moduleQs = module.questions || [];
          const relevantQuestions = moduleQs.filter((q: Question) => q.respondentType === 'Self');
          // Consider product evidence requirement
          const evidenceRequired = (assessment as any).type?.toLowerCase() === 'product';
          const hasEvidence = !!(
            (assessment as any).moduleEvidence &&
            (assessment as any).moduleEvidence[module.moduleID] &&
            (assessment as any).moduleEvidence[module.moduleID].length > 0
          );
          const needsEvidence = !!(evidenceRequired && !hasEvidence);

          const unansweredQuestions = relevantQuestions.filter((question: Question) => {
            const isAnswered =
              questionCompletionStatus[module.moduleID]?.team?.[question.trAssessmentQuestionID];
            return !isAnswered;
          });
          const totalQuestions = relevantQuestions.length;
          const completed = totalQuestions - unansweredQuestions.length;

          if (unansweredQuestions.length === 0 && !needsEvidence) return null;

          return { module, unansweredQuestions, totalQuestions, completed, needsEvidence };
        })
        .filter(
          (
            x: unknown
          ): x is {
            module: Module;
            unansweredQuestions: Question[];
            totalQuestions: number;
            completed: number;
            needsEvidence?: boolean;
          } => Boolean(x)
        );
    }

    // If no missing in any flow, return null
    const hasMissingIndividu = tree.some((p) =>
      p.modules.some((m) => m.unansweredQuestions.length > 0)
    );
    const hasMissingTeam = teamModules.some((m) => m.unansweredQuestions.length > 0);
    const hasMissingParticipant = participantModules.some(
      (m) => m.unansweredQuestions.length > 0 || m.needsEvidence
    );
    if (!hasMissingIndividu && !hasMissingTeam && !hasMissingParticipant) return null;

    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        title='Assessment Incomplete'
        color='orange'
        mb='md'
      >
        <Stack gap='sm'>
          <Text size='sm' fw={500}>
            Please complete the following before submitting:
          </Text>
          {/* Respondent (Individu): Participant > Module > Questions; Participant owner: Module > Questions (+ Evidence); Else Team: Module > Questions */}
          {assessment.typeAssessment === 'Individu' && owner?.type === 'Respondent' ? (
            <Accordion variant='contained'>
              {tree.map(({ participant, modules }) => {
                const participantId = participant.binusianId || participant.binusianID;
                const isCurrentParticipant = participantId === activeParticipant;
                return (
                  <Accordion.Item key={participantId ?? ''} value={participantId ?? ''}>
                    <Accordion.Control>
                      <Group justify='space-between'>
                        <Group gap='xs'>
                          {isCurrentParticipant && <IconUser size={14} color='#2196f3' />}
                          <Text size='sm' fw={isCurrentParticipant ? 600 : 400}>
                            {participant.binusianName || participant.name || participantId}
                            {isCurrentParticipant && ' (Selected)'}
                          </Text>
                          {(() => {
                            // Participant-level progress across all modules in this tree entry
                            const totals = modules.reduce(
                              (acc: { completed: number; total: number }, m) => {
                                const total = m.totalQuestions ?? 0;
                                const fallbackDone = Math.max(
                                  0,
                                  total - (m.unansweredQuestions?.length || 0)
                                );
                                const done =
                                  typeof m.completed === 'number' ? m.completed : fallbackDone;
                                return {
                                  completed: acc.completed + done,
                                  total: acc.total + total
                                };
                              },
                              { completed: 0, total: 0 }
                            );
                            const pct =
                              totals.total > 0
                                ? Math.round((totals.completed / totals.total) * 100)
                                : 100;
                            return (
                              <Badge
                                size='xs'
                                color={pct === 100 ? 'green' : pct > 0 ? 'yellow' : 'red'}
                                variant='light'
                              >
                                {pct}%
                              </Badge>
                            );
                          })()}
                        </Group>
                        <Tooltip label='Switch participant' withArrow withinPortal position='top'>
                          <ActionIcon
                            size='sm'
                            color={isCurrentParticipant ? 'blue' : 'gray'}
                            variant={isCurrentParticipant ? 'filled' : 'outline'}
                            onClick={() => {
                              setActiveParticipant(participantId ?? null);
                              // Scroll to first question after switching participant
                              setTimeout(() => {
                                // Find first module with unanswered questions
                                const firstModule = modules[0]?.module;
                                if (firstModule) {
                                  setActiveModule(firstModule.moduleID ?? null);
                                  setTimeout(() => {
                                    const firstQuestion = modules[0]?.unansweredQuestions[0];
                                    if (firstQuestion) {
                                      const el = document.getElementById(
                                        `question-${firstModule.moduleID}-${firstQuestion.trAssessmentQuestionID}`
                                      );
                                      if (el) {
                                        el.scrollIntoView({
                                          behavior: 'smooth',
                                          block: 'center'
                                        });
                                      } else if (questionSectionRef.current) {
                                        questionSectionRef.current.scrollIntoView({
                                          behavior: 'smooth',
                                          block: 'start'
                                        });
                                      }
                                    }
                                  }, 100);
                                }
                              }, 100);
                            }}
                          >
                            <IconUser size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Accordion>
                        {modules.map(
                          ({ module, unansweredQuestions, totalQuestions, completed }) => {
                            const isCurrentModule = module.moduleID === activeModule;
                            const isCurrentModuleForActiveParticipant =
                              isCurrentModule && isCurrentParticipant;
                            const total = totalQuestions ?? 0;
                            const fallbackDone = Math.max(
                              0,
                              total - (unansweredQuestions?.length || 0)
                            );
                            const done = typeof completed === 'number' ? completed : fallbackDone;
                            return (
                              <Accordion.Item key={module.moduleID} value={module.moduleID}>
                                <Accordion.Control>
                                  <Group justify='space-between'>
                                    <Text
                                      size='sm'
                                      fw={isCurrentModuleForActiveParticipant ? 600 : 400}
                                    >
                                      {module.moduleName || module.moduleID}
                                    </Text>
                                    <Group gap='xs'>
                                      <Tooltip
                                        label='Incomplete'
                                        withArrow
                                        withinPortal
                                        position='top'
                                      >
                                        <Badge
                                          color={(() => {
                                            if (total === 0) return 'gray';
                                            if (done === total) return 'green';
                                            return done > 0 ? 'yellow' : 'red';
                                          })()}
                                          size='xs'
                                        >
                                          {`${done}/${total}`}
                                        </Badge>
                                      </Tooltip>
                                      <Tooltip
                                        label='Go to this module'
                                        withArrow
                                        withinPortal
                                        position='top'
                                      >
                                        <ActionIcon
                                          size='sm'
                                          color={
                                            isCurrentModuleForActiveParticipant ? 'orange' : 'gray'
                                          }
                                          variant={
                                            isCurrentModuleForActiveParticipant
                                              ? 'filled'
                                              : 'outline'
                                          }
                                          onClick={() => {
                                            // Ensure participant switches to the clicked participant's context
                                            setActiveParticipant(participantId ?? null);
                                            setActiveModule(module.moduleID ?? null);
                                            // Scroll to first question after switching module
                                            setTimeout(() => {
                                              const firstQuestion = unansweredQuestions[0];
                                              if (firstQuestion) {
                                                const el = document.getElementById(
                                                  `question-${module.moduleID}-${firstQuestion.trAssessmentQuestionID}`
                                                );
                                                if (el) {
                                                  el.scrollIntoView({
                                                    behavior: 'smooth',
                                                    block: 'center'
                                                  });
                                                } else if (questionSectionRef.current) {
                                                  questionSectionRef.current.scrollIntoView({
                                                    behavior: 'smooth',
                                                    block: 'start'
                                                  });
                                                }
                                              }
                                            }, 100);
                                          }}
                                        >
                                          <IconArrowRight size={16} />
                                        </ActionIcon>
                                      </Tooltip>
                                    </Group>
                                  </Group>
                                </Accordion.Control>
                                <Accordion.Panel>
                                  <List size='xs' spacing={2}>
                                    {unansweredQuestions.map((question, qIndex) => (
                                      <List.Item key={qIndex}>
                                        <Group justify='space-between'>
                                          <Text
                                            size='xs'
                                            c='blue'
                                            style={{
                                              cursor: 'pointer',
                                              textDecoration: 'underline'
                                            }}
                                            onClick={() => {
                                              setActiveModule(module.moduleID ?? null);
                                              setActiveParticipant(participantId ?? null);
                                              setTimeout(() => {
                                                const el = document.getElementById(
                                                  `question-${module.moduleID}-${question.trAssessmentQuestionID}`
                                                );
                                                if (el) {
                                                  el.scrollIntoView({
                                                    behavior: 'smooth',
                                                    block: 'center'
                                                  });
                                                } else if (questionSectionRef.current) {
                                                  questionSectionRef.current.scrollIntoView({
                                                    behavior: 'smooth',
                                                    block: 'start'
                                                  });
                                                }
                                              }, 200); // Increased timeout for reliability
                                            }}
                                            title='Go to Question'
                                          >
                                            {question.title.length > 80
                                              ? `${question.title.substring(0, 80)}...`
                                              : question.title}
                                          </Text>
                                        </Group>
                                      </List.Item>
                                    ))}
                                  </List>
                                </Accordion.Panel>
                              </Accordion.Item>
                            );
                          }
                        )}
                      </Accordion>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          ) : owner?.type === 'Participant' ? (
            <Accordion variant='contained'>
              {participantModules.map(
                ({ module, unansweredQuestions, totalQuestions, completed, needsEvidence }) => {
                  const isCurrentModule = module.moduleID === activeModule;
                  const total = totalQuestions ?? 0;
                  const fallbackDone = Math.max(0, total - (unansweredQuestions?.length || 0));
                  const done = typeof completed === 'number' ? completed : fallbackDone;
                  return (
                    <Accordion.Item key={module.moduleID} value={module.moduleID}>
                      <Accordion.Control>
                        <Group justify='space-between'>
                          <Text size='sm' fw={isCurrentModule ? 600 : 400}>
                            {module.moduleName || module.moduleID}
                          </Text>
                          <Group gap='xs'>
                            {needsEvidence && (
                              <Badge color='red' size='xs' variant='light'>
                                Evidence required
                              </Badge>
                            )}
                            <Tooltip label='Incomplete' withArrow withinPortal position='top'>
                              <Badge
                                color={(() => {
                                  if (total === 0) return 'gray';
                                  if (done === total) return 'green';
                                  return done > 0 ? 'yellow' : 'red';
                                })()}
                                size='xs'
                              >
                                {`${done}/${total}`}
                              </Badge>
                            </Tooltip>
                            <Tooltip
                              label='Go to this module'
                              withArrow
                              withinPortal
                              position='top'
                            >
                              <ActionIcon
                                size='sm'
                                color={isCurrentModule ? 'orange' : 'gray'}
                                variant={isCurrentModule ? 'filled' : 'outline'}
                                onClick={() => {
                                  setActiveModule(module.moduleID ?? null);
                                  setTimeout(() => {
                                    const firstQuestion = unansweredQuestions[0];
                                    if (firstQuestion) {
                                      const el = document.getElementById(
                                        `question-${module.moduleID}-${firstQuestion.trAssessmentQuestionID}`
                                      );
                                      if (el) {
                                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                      } else if (questionSectionRef.current) {
                                        questionSectionRef.current.scrollIntoView({
                                          behavior: 'smooth',
                                          block: 'start'
                                        });
                                      }
                                    } else if (needsEvidence && questionSectionRef.current) {
                                      // No questions; evidence section likely near top
                                      questionSectionRef.current.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'start'
                                      });
                                    }
                                  }, 100);
                                }}
                              >
                                <IconArrowRight size={16} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Group>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack gap='xs'>
                          {unansweredQuestions.length > 0 && (
                            <List size='xs' spacing={2}>
                              {unansweredQuestions.map((question, qIndex) => (
                                <List.Item key={qIndex}>
                                  <Text
                                    size='xs'
                                    c='blue'
                                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                    onClick={() => {
                                      setActiveModule(module.moduleID ?? null);
                                      setTimeout(() => {
                                        const el = document.getElementById(
                                          `question-${module.moduleID}-${question.trAssessmentQuestionID}`
                                        );
                                        if (el) {
                                          el.scrollIntoView({
                                            behavior: 'smooth',
                                            block: 'center'
                                          });
                                        } else if (questionSectionRef.current) {
                                          questionSectionRef.current.scrollIntoView({
                                            behavior: 'smooth',
                                            block: 'start'
                                          });
                                        }
                                      }, 200);
                                    }}
                                    title='Go to Question'
                                  >
                                    {question.title.length > 80
                                      ? `${question.title.substring(0, 80)}...`
                                      : question.title}
                                  </Text>
                                </List.Item>
                              ))}
                            </List>
                          )}
                          {needsEvidence && (
                            <Text size='xs' c='red'>
                              Please upload at least one evidence file for this module.
                            </Text>
                          )}
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  );
                }
              )}
            </Accordion>
          ) : (
            <Accordion variant='contained'>
              {teamModules.map(({ module, unansweredQuestions, totalQuestions, completed }) => {
                const isCurrentModule = module.moduleID === activeModule;
                const total = totalQuestions ?? 0;
                const fallbackDone = Math.max(0, total - (unansweredQuestions?.length || 0));
                const done = typeof completed === 'number' ? completed : fallbackDone;
                return (
                  <Accordion.Item key={module.moduleID} value={module.moduleID}>
                    <Accordion.Control>
                      <Group justify='space-between'>
                        <Text size='sm' fw={isCurrentModule ? 600 : 400}>
                          {module.moduleName || module.moduleID}
                        </Text>
                        <Group gap='xs'>
                          <Tooltip label='Incomplete' withArrow withinPortal position='top'>
                            <Badge
                              color={(() => {
                                if (total === 0) return 'gray';
                                if (done === total) return 'green';
                                return done > 0 ? 'yellow' : 'red';
                              })()}
                              size='xs'
                            >
                              {`${done}/${total}`}
                            </Badge>
                          </Tooltip>
                          <Tooltip
                            label='Go to this module (first unanswered)'
                            withArrow
                            withinPortal
                            position='top'
                          >
                            <ActionIcon
                              size='sm'
                              color={isCurrentModule ? 'orange' : 'gray'}
                              variant={isCurrentModule ? 'filled' : 'outline'}
                              onClick={() => {
                                setActiveModule(module.moduleID ?? null);
                                setTimeout(() => {
                                  const firstQuestion = unansweredQuestions[0];
                                  if (firstQuestion) {
                                    const el = document.getElementById(
                                      `question-${module.moduleID}-${firstQuestion.trAssessmentQuestionID}`
                                    );
                                    if (el) {
                                      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    } else if (questionSectionRef.current) {
                                      questionSectionRef.current.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'start'
                                      });
                                    }
                                  }
                                }, 100);
                              }}
                            >
                              <IconArrowRight size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <List size='xs' spacing={2}>
                        {unansweredQuestions.map((question, qIndex) => (
                          <List.Item key={qIndex}>
                            <Group justify='space-between'>
                              <Text
                                size='xs'
                                c='blue'
                                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => {
                                  setActiveModule(module.moduleID ?? null);
                                  setTimeout(() => {
                                    const el = document.getElementById(
                                      `question-${module.moduleID}-${question.trAssessmentQuestionID}`
                                    );
                                    if (el) {
                                      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    } else if (questionSectionRef.current) {
                                      questionSectionRef.current.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'start'
                                      });
                                    }
                                  }, 200);
                                }}
                                title='Go to Question'
                              >
                                {question.title.length > 80
                                  ? `${question.title.substring(0, 80)}...`
                                  : question.title}
                              </Text>
                            </Group>
                          </List.Item>
                        ))}
                      </List>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          )}
        </Stack>
      </Alert>
    );
  };

  useEffect(() => {
    const allowedModules = getParticipantAllowedModules();
    // Determine visible modules (with relevant questions for owner)
    const visibleModules = allowedModules.filter((module: Module) => {
      const ownerRespondentType = owner?.respondentType || '';
      const moduleQs = module.questions || [];
      let relevantQuestions: Question[] = [];
      if (owner?.type === 'Participant') {
        relevantQuestions = moduleQs.filter((q: Question) => q.respondentType === 'Self');
      } else {
        relevantQuestions = moduleQs.filter((q: Question) =>
          owner?.isSelfAssess
            ? q.respondentType === 'Self' || q.respondentType === ownerRespondentType
            : q.respondentType === ownerRespondentType
        );
      }
      return relevantQuestions.length > 0;
    });

    // If no active module or current active module is no longer visible, pick the first visible
    if (
      (!activeModule || !visibleModules.some((m: Module) => m.moduleID === activeModule)) &&
      visibleModules.length > 0
    ) {
      setActiveModule(visibleModules[0].moduleID);
    }

    if (assessment?.typeAssessment === 'Individu' && owner?.type === 'Respondent') {
      if (assessment.owner.participants?.length > 0 && !activeParticipant) {
        setActiveParticipant(assessment.owner.participants[0].binusianId);
      }
    } else if (assessment?.typeAssessment !== 'Individu') {
      setActiveParticipant(null);
    }
  }, [assessment, activeModule, activeParticipant, owner]);

  const getQuestionGroups = (questions: Question[]) => {
    const groups: Record<string, Question[]> = {};

    questions.forEach((question) => {
      let groupName: string;

      if (question.respondentType) {
        groupName = question.respondentType;
      } else if (question.title.includes('anda mengalami peningkatan')) {
        groupName = 'Self Assessment';
      } else if (question.title.includes('Tim saya mampu')) {
        groupName = 'Team Assessment';
      } else if (question.title.includes('karyawan dibawah ini mampu')) {
        groupName = 'Peer Assessment';
      } else {
        groupName = 'Other Questions';
      }

      if (!groups[groupName]) {
        groups[groupName] = [];
      }

      groups[groupName].push(question);
    });

    // Shuffle questions inside each group when assessment.isRandom === 'Y'
    const isRandomEnabled = (assessment as any)?.isRandom === 'Y';

    if (isRandomEnabled) {
      // Deterministic hash to keep order stable across renders while still randomized
      const hashString = (str: string) => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
          hash = (hash << 5) - hash + str.charCodeAt(i);
          hash |= 0; // Convert to 32bit integer
        }
        return hash;
      };

      Object.keys(groups).forEach((key) => {
        groups[key] = groups[key].slice().sort((a, b) => {
          return hashString(a.trAssessmentQuestionID) - hashString(b.trAssessmentQuestionID);
        });
      });
    }

    return groups;
  };

  useEffect(() => {
    if (assessment?.modules?.length > 0) {
      const initialResponses: Record<string, Record<string, Record<string, string>>> = {};

      assessment.modules.forEach((module: Module) => {
        initialResponses[module.moduleID] = {};

        if (assessment.typeAssessment === 'Individu' && assessment.owner.participants) {
          assessment.owner.participants.forEach((participant: Participant) => {
            initialResponses[module.moduleID][participant.binusianId] = {};
          });
        } else {
          initialResponses[module.moduleID] = { team: {} };
        }
      });

      const draftKey = `${process.env.DRAFT_STORAGE_KEY}${encryptedEmail}`;
      const savedDraft = localStorage.getItem(draftKey);

      if (savedDraft) {
        try {
          const parsedDraft = JSON.parse(savedDraft);
          // Merge parsed draft with initial responses
          const mergedResponses = { ...initialResponses };

          // Deep merge the draft responses
          Object.keys(parsedDraft).forEach((moduleId) => {
            if (!mergedResponses[moduleId]) {
              mergedResponses[moduleId] = {};
            }
            Object.keys(parsedDraft[moduleId]).forEach((participantKey) => {
              if (!mergedResponses[moduleId][participantKey]) {
                mergedResponses[moduleId][participantKey] = {};
              }
              mergedResponses[moduleId][participantKey] = {
                ...mergedResponses[moduleId][participantKey],
                ...parsedDraft[moduleId][participantKey]
              };
            });
          });

          setResponses(mergedResponses);

          if (assessment.typeAssessment === 'Individu' && assessment.owner.participants) {
            assessment.modules.forEach((module: Module) => {
              assessment.owner.participants.forEach((participant: Participant) => {
                const participantResponses =
                  parsedDraft[module.moduleID]?.[participant.binusianId] || {};
                checkModuleCompletion(
                  module.moduleID,
                  participantResponses,
                  participant.binusianId
                );
              });
            });
          } else {
            assessment.modules.forEach((module: Module) => {
              const teamResponses = parsedDraft[module.moduleID]?.team || {};
              checkModuleCompletion(module.moduleID, teamResponses);
            });
          }
        } catch {
          setResponses(initialResponses);
        }
      } else {
        setResponses(initialResponses);
      }
    }
  }, [assessment, encryptedEmail]);

  const checkModuleCompletion = (
    moduleId: string,
    moduleResponses: Record<string, string>,
    participantId?: string
  ) => {
    if (!assessment) return false;

    const currentModule = assessment.modules?.find((m: Module) => m.moduleID === moduleId);
    if (!currentModule || !currentModule.questions || currentModule.questions.length === 0) {
      return false;
    }

    const ownerRespondentType = owner?.respondentType || '';

    let relevantQuestions;
    relevantQuestions = currentModule.questions.filter((q: Question) =>
      owner?.isSelfAssess
        ? q.respondentType === 'Self' || q.respondentType === ownerRespondentType
        : q.respondentType === ownerRespondentType
    );

    if (relevantQuestions.length === 0) return true;

    const answeredQuestions = relevantQuestions.filter(
      (q: Question) =>
        moduleResponses[q.trAssessmentQuestionID] &&
        moduleResponses[q.trAssessmentQuestionID].trim() !== ''
    );

    const isComplete = answeredQuestions.length === relevantQuestions.length;

    const completionKey = participantId ? `${moduleId}_${participantId}` : moduleId;

    setCompletedModules((prev) => {
      if (isComplete && !prev.includes(completionKey)) {
        return [...prev, completionKey];
      } else if (!isComplete && prev.includes(completionKey)) {
        return prev.filter((id) => id !== completionKey);
      }
      return prev;
    });

    return isComplete;
  };

  useEffect(() => {
    if (assessment?.modules) {
      if (assessment.typeAssessment === 'Individu' && assessment.owner.participants) {
        assessment.modules.forEach((module: Module) => {
          assessment.owner.participants.forEach((participant: Participant) => {
            const participantResponses = responses[module.moduleID]?.[participant.binusianId] || {};
            checkModuleCompletion(module.moduleID, participantResponses, participant.binusianId);
          });
        });
      } else {
        assessment.modules.forEach((module: Module) => {
          const teamResponses = responses[module.moduleID]?.team || {};
          checkModuleCompletion(module.moduleID, teamResponses);
        });
      }
    }
  }, [responses, assessment]);

  const handleResponseChange = (
    moduleId: string,
    questionId: string,
    value: string,
    participantId?: string
  ) => {
    setResponses((prev: any) => {
      const updatedResponses = { ...prev };

      if (assessment?.typeAssessment === 'Individu' && participantId) {
        if (!updatedResponses[moduleId]) {
          updatedResponses[moduleId] = {};
        }

        if (!updatedResponses[moduleId][participantId]) {
          updatedResponses[moduleId][participantId] = {};
        }

        updatedResponses[moduleId][participantId][questionId] = value;
      } else {
        if (!updatedResponses[moduleId]) {
          updatedResponses[moduleId] = {};
        }

        if (!updatedResponses[moduleId].team) {
          updatedResponses[moduleId].team = {};
        }

        updatedResponses[moduleId].team[questionId] = value;
      }

      setDraftSaved(false);
      return updatedResponses;
    });

    if (owner?.type === 'Participant') {
      setTimeout(() => {
        setQuestionCompletionStatus((prev) => {
          const newStatus = { ...prev };

          if (!newStatus[moduleId]) {
            newStatus[moduleId] = {};
          }

          if (!newStatus[moduleId]['team']) {
            newStatus[moduleId]['team'] = {};
          }

          // Update the specific question status
          newStatus[moduleId]['team'][questionId] = !!(value && value.trim() !== '');

          return newStatus;
        });
      }, 0);
    }
  };

  const cleanupResponses = () => {
    setResponses((prev: any) => {
      const cleaned = { ...prev };

      Object.keys(cleaned).forEach((moduleId) => {
        if (cleaned[moduleId].undefined) {
          delete cleaned[moduleId].undefined;
        }
      });

      return cleaned;
    });
  };

  useEffect(() => {
    if (responses) {
      cleanupResponses();
    }
  }, []);

  const saveDraft = () => {
    showSuccessNotification({
      message: 'Draft saved successfully'
    });

    localStorage.setItem(
      `${process.env.DRAFT_STORAGE_KEY}${encryptedEmail}`,
      JSON.stringify(responses)
    );
    setDraftSaved(true);

    setTimeout(() => {
      setDraftSaved(false);
    }, 3000);
  };

  const submitMutation = useMutation({
    mutationFn: (responseData: any) =>
      assessmentScheduleService.submitAssessmentResponseByEmail(encryptedEmail, responseData),
    onSuccess: () => {
      setFormSubmitted(true);
      setIsSubmitting(false);
      isSubmittingRef.current = false;
      localStorage.removeItem(`${process.env.DRAFT_STORAGE_KEY}${encryptedEmail}`);
      showSuccessNotification({
        message: 'Assessment submitted successfully'
      });
      refetch();
    },
    onError: (error: any) => {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
      setFormError(error.message || 'Failed to submit your assessment. Please try again.');
      showErrorNotification({
        message: 'Error submitting assessment'
      });
    }
  });

  const handleSubmit = () => {
    if (!assessment) return;
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    setIsSubmitting(true);

    let responseData: any = {
      msAssessmentScheduleID: assessment.msAssessmentScheduleID,
      hash: encryptedEmail,
      typeAssessment: assessment.typeAssessment || 'Team'
    };

    if (assessment.typeAssessment === 'Individu' && assessment.owner.participants) {
      const participantResponses = [];

      for (const participant of assessment.owner.participants) {
        const participantModules = [];
        let hasAnyResponses = false;

        for (const module of assessment.modules) {
          const participantId = participant.binusianId || participant.binusianID;
          const participantModuleResponses = responses[module.moduleID]?.[participantId] || {};

          if (Object.keys(participantModuleResponses).length === 0) continue;

          const moduleQs = module.questions || [];

          const questions = moduleQs
            .map((question: Question) => {
              const answer = participantModuleResponses[question.trAssessmentQuestionID] || '';
              return {
                trAssessmentQuestionID: question.trAssessmentQuestionID,
                answer: answer,
                type: question.type
              };
            })
            .filter((q: any) => q.answer !== '');

          if (questions.length === 0) continue;

          hasAnyResponses = true;

          const evidences: Evidence[] = [];
          if (assessment.moduleEvidence && assessment.moduleEvidence[module.moduleID]) {
            assessment.moduleEvidence[module.moduleID].forEach((evidence: Evidence) => {
              evidences.push({
                evidenceUrl: evidence.evidenceUrl,
                fileName: evidence.fileName,
                fileSize: evidence.fileSize,
                uploadDate: evidence.uploadDate
              });
            });
          }

          participantModules.push({
            moduleID: module.moduleID,
            trAssessmentModuleID: module.trAssessmentModuleID,
            questions,
            evidences
          });
        }

        if (hasAnyResponses) {
          participantResponses.push({
            trAssessmentParticipantID: participant.trAssessmentParticipantID,
            modules: participantModules
          });
        }
      }

      responseData.participantResponses = participantResponses;
    } else {
      const modules = [];

      for (const module of assessment.modules) {
        const moduleQs = module.questions || [];
        const teamResponses = responses[module.moduleID]?.team || {};

        if (Object.keys(teamResponses).length === 0) continue;

        const questions = moduleQs
          .map((question: any) => ({
            trAssessmentQuestionID: question.trAssessmentQuestionID,
            answer: teamResponses[question.trAssessmentQuestionID] || '',
            type: question.type
          }))
          .filter((q: any) => q.answer !== '');

        if (questions.length === 0) continue;

        const evidences: {
          evidenceUrl: string;
          fileName: string;
          fileSize: string;
          uploadDate: string;
        }[] = [];

        if (assessment.moduleEvidence && assessment.moduleEvidence[module.moduleID]) {
          assessment.moduleEvidence[module.moduleID].forEach((evidence: Evidence) => {
            evidences.push({
              evidenceUrl: evidence.evidenceUrl,
              fileName: evidence.fileName,
              fileSize: evidence.fileSize,
              uploadDate: evidence.uploadDate
            });
          });
        }

        modules.push({
          moduleID: module.moduleID,
          trAssessmentModuleID: module.trAssessmentModuleID,
          questions,
          evidences
        });
      }

      responseData.modules = modules;
    }

    submitMutation.mutate(responseData);
  };

  const handleModuleFileSelect = (moduleId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const selectedFile = files[0];

    if (selectedFile.size > 20 * 1024 * 1024) {
      showErrorNotification({
        message: 'File size exceeds the limit. Please upload a file smaller than 20 MB.'
      });
      return;
    }

    setModuleEvidenceFiles((prev) => ({
      ...prev,
      [moduleId]: [...(prev[moduleId] || []), selectedFile]
    }));

    if (event.target) event.target.value = '';
  };

  const handleRemoveModuleFile = (moduleId: string, fileIndex: number) => {
    setModuleEvidenceFiles((prev) => {
      const updatedFiles = [...(prev[moduleId] || [])];
      updatedFiles.splice(fileIndex, 1);

      return {
        ...prev,
        [moduleId]: updatedFiles
      };
    });
  };

  const handleSubmitModuleEvidence = async (moduleId: string) => {
    const files = moduleEvidenceFiles[moduleId];

    if (!files || files.length === 0) {
      showErrorNotification({
        message: 'Please select at least one file to upload as evidence.'
      });
      return;
    }

    setUploadingModule(moduleId);

    try {
      const updatedAssessment = { ...assessment };

      if (!updatedAssessment.moduleEvidence) {
        updatedAssessment.moduleEvidence = {};
      }

      if (!updatedAssessment.moduleEvidence[moduleId]) {
        updatedAssessment.moduleEvidence[moduleId] = [];
      }

      for (const file of files) {
        const formData = new FormData();
        formData.append('hash', encryptedEmail);
        formData.append('moduleID', moduleId);
        formData.append('file', file);

        const uploadData = await assessmentScheduleService.uploadModuleEvidence(formData);

        if (uploadData.resultCode !== 200 || !uploadData.data?.fileUrl) {
          throw new Error(`Failed to upload file ${file.name}`);
        }

        const newEvidence = {
          evidenceUrl: uploadData.data.fileUrl,
          fileName: uploadData.data.fileName || file.name,
          fileSize: uploadData.data.fileSize || `${(file.size / (1024 * 1024)).toFixed(2)} MB`,
          uploadDate: uploadData.data.uploadDate || new Date().toISOString()
        };

        updatedAssessment.moduleEvidence[moduleId].push(newEvidence);
      }

      const assessmentCopy = { ...assessment };
      assessmentCopy.moduleEvidence = updatedAssessment.moduleEvidence;

      if (typeof refetch === 'function') {
        Object.assign(assessment, assessmentCopy);
      }

      showSuccessNotification({
        message: 'Evidence submitted successfully'
      });

      setModuleEvidenceFiles((prev) => ({
        ...prev,
        [moduleId]: []
      }));
    } catch (error) {
      showErrorNotification({
        message:
          error instanceof Error ? error.message : 'Failed to upload evidence. Please try again.'
      });
    } finally {
      setUploadingModule(null);
    }
  };

  const getFileExtension = (filename: string) => {
    return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);
  };

  const getFileIcon = (filename: string) => {
    const ext = getFileExtension(filename).toLowerCase();

    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
      return <IconFile size={24} color='#4299e1' />;
    } else if (['pdf'].includes(ext)) {
      return <IconFile size={24} color='#e53e3e' />;
    } else if (['doc', 'docx'].includes(ext)) {
      return <IconFile size={24} color='#3182ce' />;
    } else if (['ppt', 'pptx'].includes(ext)) {
      return <IconFile size={24} color='#dd6b20' />;
    } else if (['xls', 'xlsx'].includes(ext)) {
      return <IconFile size={24} color='#38a169' />;
    } else {
      return <IconFile size={24} />;
    }
  };

  const findNextIncompleteModule = () => {
    if (!assessment?.modules || !assessment.modules.length) return null;

    const ownerRespondentType = owner?.respondentType || '';

    // Use filtered modules for Participants
    const modulesToCheck =
      owner?.type === 'Participant' ? getParticipantAllowedModules() : assessment.modules;

    for (const module of modulesToCheck) {
      const moduleId = module.moduleID;

      if (moduleId === activeModule) continue;

      const questions = module.questions || [];

      let relevantQuestions;
      relevantQuestions = questions.filter((q: Question) =>
        owner?.isSelfAssess
          ? q.respondentType === 'Self' || q.respondentType === ownerRespondentType
          : q.respondentType === ownerRespondentType
      );

      if (relevantQuestions.length === 0) continue;

      if (assessment.typeAssessment === 'Individu' && assessment.owner.participants) {
        const currentParticipant =
          activeParticipant || assessment.owner.participants[0]?.binusianID;
        if (!currentParticipant) continue;

        const participantResponses = responses[moduleId]?.[currentParticipant] || {};
        const allAnswered = relevantQuestions.every(
          (q: Question) =>
            participantResponses[q.trAssessmentQuestionID] &&
            participantResponses[q.trAssessmentQuestionID].trim() !== ''
        );

        if (!allAnswered) return moduleId;
      } else {
        const teamResponses = responses[moduleId]?.team || {};
        const allAnswered = relevantQuestions.every(
          (q: Question) =>
            teamResponses[q.trAssessmentQuestionID] &&
            teamResponses[q.trAssessmentQuestionID].trim() !== ''
        );

        if (!allAnswered) return moduleId;
      }
    }

    return null;
  };

  const findNextModuleForEvidence = () => {
    if (!assessment?.modules || !assessment.modules.length) return null;

    // Use filtered modules for Participants
    const modulesToCheck =
      owner?.type === 'Participant' ? getParticipantAllowedModules() : assessment.modules;

    for (const module of modulesToCheck) {
      const moduleId = module.moduleID;

      if (moduleId === activeModule) continue;

      const hasEvidence =
        assessment.moduleEvidence &&
        assessment.moduleEvidence[moduleId] &&
        assessment.moduleEvidence[moduleId].length > 0;

      if (!hasEvidence && assessment.type?.toLowerCase() === 'product') {
        return moduleId;
      }
    }

    return null;
  };

  const handleSubmitOrNavigate = () => {
    setFormError('');

    if (formSubmitted || isSubmitting || isSubmittingRef.current) return;

    // Check participant selection for Individual assessments
    if (owner?.type === 'Respondent' && assessment?.typeAssessment === 'Individu') {
      if (!activeParticipant) {
        setFormError('Please select a participant to evaluate from the dropdown above.');
        return;
      }
    }

    // Check evidence validation for participants
    if (owner?.type === 'Participant' && assessment?.type?.toLowerCase() === 'product') {
      if (!validateParticipantEvidence(assessment, activeModule)) {
        setFormError('Please upload at least one evidence file for this module before proceeding.');
        return;
      }
    }

    // Check Individual assessment completion for respondents
    if (owner?.type === 'Respondent' && assessment?.typeAssessment === 'Individu') {
      if (!validateIndividualAssessmentCompletion(assessment, responses)) {
        setFormError('Please complete assessment for all participants before submitting.');
        return;
      }
    }

    // Enhanced current module completion check
    const getCurrentModuleCompletion = () => {
      if (!activeModule) return { complete: false, details: 'No active module' };

      const module = assessment.modules.find((m: Module) => m.moduleID === activeModule);
      if (!module) return { complete: false, details: 'Module not found' };

      const ownerRespondentType = owner?.respondentType || '';
      let relevantQuestions: Question[] = [];

      if (owner?.type === 'Participant') {
        relevantQuestions = (module.questions || []).filter(
          (q: Question) => q.respondentType === 'Self'
        );
      } else {
        relevantQuestions = (module.questions || []).filter((q: Question) =>
          owner?.isSelfAssess
            ? q.respondentType === 'Self' || q.respondentType === ownerRespondentType
            : q.respondentType === ownerRespondentType
        );
      }

      if (relevantQuestions.length === 0) {
        return { complete: true, details: 'No questions for your role' };
      }

      const unansweredQuestions: string[] = [];

      if (
        assessment.typeAssessment === 'Individu' &&
        activeParticipant &&
        owner?.type === 'Respondent'
      ) {
        relevantQuestions.forEach((q: Question) => {
          const isAnswered =
            questionCompletionStatus[activeModule!]?.[activeParticipant]?.[
              q.trAssessmentQuestionID
            ];
          if (!isAnswered) {
            unansweredQuestions.push(q.title);
          }
        });
      } else {
        relevantQuestions.forEach((q: Question) => {
          const isAnswered =
            questionCompletionStatus[activeModule!]?.['team']?.[q.trAssessmentQuestionID];
          if (!isAnswered) {
            unansweredQuestions.push(q.title);
          }
        });
      }

      return {
        complete: unansweredQuestions.length === 0,
        details:
          unansweredQuestions.length > 0
            ? `${unansweredQuestions.length} unanswered questions: ${unansweredQuestions.slice(0, 2).join(', ')}${unansweredQuestions.length > 2 ? '...' : ''}`
            : 'All questions answered'
      };
    };

    const currentModuleStatus = getCurrentModuleCompletion();

    if (!currentModuleStatus.complete) {
      setFormError(`Current module incomplete: ${currentModuleStatus.details}`);
      return;
    }

    // Find next incomplete module with detailed logic
    const findNextIncompleteModuleDetailed = () => {
      if (!assessment?.modules || !assessment.modules.length) return null;

      // Use filtered modules for Participants
      const modulesToCheck =
        owner?.type === 'Participant' ? getParticipantAllowedModules() : assessment.modules;

      for (const module of modulesToCheck) {
        const moduleId = module.moduleID;
        if (moduleId === activeModule) continue;

        const ownerRespondentType = owner?.respondentType || '';
        let relevantQuestions: Question[] = [];

        if (owner?.type === 'Participant') {
          relevantQuestions = (module.questions || []).filter(
            (q: Question) => q.respondentType === 'Self'
          );
        } else {
          relevantQuestions = (module.questions || []).filter((q: Question) =>
            owner?.isSelfAssess
              ? q.respondentType === 'Self' || q.respondentType === ownerRespondentType
              : q.respondentType === ownerRespondentType
          );
        }

        if (relevantQuestions.length === 0) continue;

        // Check if module is incomplete
        let hasUnanswered = false;

        if (
          assessment.typeAssessment === 'Individu' &&
          assessment.owner.participants &&
          owner?.type === 'Respondent'
        ) {
          // Check all participants for this module
          for (const participant of assessment.owner.participants) {
            const participantId = participant.binusianId || participant.binusianID;
            if (!participantId) continue;

            const hasUnansweredForParticipant = relevantQuestions.some(
              (q: Question) =>
                !questionCompletionStatus[moduleId]?.[participantId]?.[q.trAssessmentQuestionID]
            );

            if (hasUnansweredForParticipant) {
              hasUnanswered = true;
              break;
            }
          }
        } else {
          hasUnanswered = relevantQuestions.some(
            (q: Question) =>
              !questionCompletionStatus[moduleId]?.['team']?.[q.trAssessmentQuestionID]
          );
        }

        if (hasUnanswered) {
          return {
            moduleId,
            moduleName: module.moduleName || moduleId
          };
        }
      }

      return null;
    };

    // Handle different user types
    if (owner && owner.type === 'Participant') {
      // Participant logic
      if (assessment.type?.toLowerCase() === 'product') {
        const hasEvidence = validateParticipantEvidence(assessment, activeModule);
        if (!hasEvidence) {
          setFormError('Please upload at least one evidence file before proceeding.');
          return;
        }
      }

      // Check if there are any incomplete modules (questions not answered)
      const nextIncompleteModule = findNextIncompleteModuleDetailed();
      if (nextIncompleteModule) {
        setActiveModule(nextIncompleteModule.moduleId);
        setTimeout(() => {
          if (questionSectionRef.current) {
            questionSectionRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }, 100);
        setFormError(
          `Please complete all questions in module "${nextIncompleteModule.moduleName}" before submitting.`
        );
        return;
      }

      // Check for evidence requirements
      const nextModuleForEvidence = findNextModuleForEvidence();
      if (nextModuleForEvidence) {
        setActiveModule(nextModuleForEvidence);
        setTimeout(() => {
          if (questionSectionRef.current) {
            questionSectionRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }, 100);
        setFormError('Please upload evidence for all required modules before submitting.');
        return;
      }

      // All validations passed, submit
      handleParticipantSubmit();
      return;
    }

    // Respondent logic
    const nextModule = findNextIncompleteModuleDetailed();

    if (nextModule) {
      setActiveModule(nextModule.moduleId);
      setTimeout(() => {
        if (questionSectionRef.current) {
          questionSectionRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
      showSuccessNotification({
        message: `Module completed! Moving to ${nextModule.moduleName}.`
      });
    } else {
      handleSubmit();
    }
  };

  const getExistingAnswers = () => {
    if (!assessment.isAnswer || !owner) return {};

    const answers: Record<string, string> = {};

    if (owner.type === 'Participant') {
      // For participants, get answers from assessment.participants array
      const currentParticipant = assessment.participants?.find(
        (p: any) => p.binusianID === owner.binusianID || p.email === owner.email
      );

      if (currentParticipant?.modules) {
        currentParticipant.modules.forEach((module: any) => {
          module.questions?.forEach((question: any) => {
            if (question.answer) {
              answers[question.trAssessmentQuestionID] = question.answer;
            }
          });
        });
      }
    } else if (owner.type === 'Respondent') {
      // Handle Individual vs Team assessment differently
      if (assessment.typeAssessment === 'Individu') {
        // For Individual assessments, get answers from respondents.responses[].participantResponses[]
        const respondent = assessment.respondents?.find(
          (r: any) => r.binusianID === owner.binusianID || r.email === owner.email
        );

        if (respondent?.responses && respondent.responses.length > 0) {
          const response = respondent.responses[0];

          const currentParticipantResponse = response.participantResponses?.find(
            (pr: any) => pr.trAssessmentParticipantID === activeParticipant
          );

          if (currentParticipantResponse?.modules) {
            currentParticipantResponse.modules.forEach((module: any) => {
              module.questions?.forEach((question: any) => {
                if (question.answer) {
                  answers[question.trAssessmentQuestionID] = question.answer;
                }
              });
            });
          }
        }
      } else {
        // For Team assessments, use the existing logic
        const respondent = assessment.respondents?.find(
          (r: any) => r.binusianID === owner.binusianID || r.email === owner.email
        );

        if (respondent?.responses && respondent.responses.length > 0) {
          const response = respondent.responses[0];
          response.modules?.forEach((module: any) => {
            module.questions?.forEach((question: any) => {
              answers[question.trAssessmentQuestionID] = question.answer || '';
            });
          });
        }
      }
    }

    return answers;
  };

  const handleParticipantSubmit = () => {
    if (!assessment || !owner || owner.type !== 'Participant') return;
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    setIsSubmitting(true);

    interface ModuleSubmitData {
      moduleID: string;
      evidences: Array<{
        evidenceUrl: string;
        fileName: string;
        fileSize: string;
        uploadDate: string;
      }>;
      questions?: Array<{
        trAssessmentQuestionID: string;
        answer: string;
        type: string;
      }>;
    }

    const modulesData: ModuleSubmitData[] = [];

    // Get only the assigned modules for this participant
    const assignedModules = getParticipantAllowedModules();

    if (assignedModules && assignedModules.length > 0) {
      assignedModules.forEach((module: Module) => {
        const moduleData: ModuleSubmitData = {
          moduleID: module.moduleID,
          evidences: []
        };

        if (assessment.moduleEvidence && assessment.moduleEvidence[module.moduleID]) {
          moduleData.evidences = assessment.moduleEvidence[module.moduleID].map(
            (evidence: Evidence) => ({
              evidenceUrl: evidence.evidenceUrl,
              fileName: evidence.fileName,
              fileSize: evidence.fileSize,
              uploadDate: evidence.uploadDate
            })
          );
        }

        const selfQuestions = module.questions?.filter((q) => q.respondentType === 'Self') || [];
        if (selfQuestions.length > 0) {
          const questions: Array<{
            trAssessmentQuestionID: string;
            answer: string;
            type: string;
          }> = [];

          selfQuestions.forEach((question) => {
            const answer =
              responses[module.moduleID]?.team?.[question.trAssessmentQuestionID] || '';
            if (answer.trim() !== '') {
              questions.push({
                trAssessmentQuestionID: question.trAssessmentQuestionID,
                answer: answer,
                type: question.type
              });
            }
          });

          if (questions.length > 0) {
            moduleData.questions = questions;
          }
        }

        if (
          (moduleData.evidences && moduleData.evidences.length > 0) ||
          (moduleData.questions && moduleData.questions.length > 0)
        ) {
          modulesData.push(moduleData);
        }
      });
    }

    const payload = {
      hash: encryptedEmail,
      modules: modulesData
    };

    if (modulesData.length > 0) {
      assessmentScheduleService
        .submitInternalEvidence(payload)
        .then(() => {
          showSuccessNotification({
            message: 'Assessment submitted successfully'
          });
          setFormSubmitted(true);
          setIsSubmitting(false);
          isSubmittingRef.current = false;
          localStorage.removeItem(`${process.env.DRAFT_STORAGE_KEY}${encryptedEmail}`);
          refetch();
        })
        .catch((error) => {
          setIsSubmitting(false);
          isSubmittingRef.current = false;
          setFormError(error.message || 'Failed to submit your assessment. Please try again.');
          showErrorNotification({
            message: 'Error submitting assessment'
          });
        });
    } else {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
      showErrorNotification({
        message: 'No data to submit. Please provide at least one answer or evidence.'
      });
    }
  };

  if (isLoading) {
    return (
      <Container fluid>
        <LoadingOverlay visible={true} />
      </Container>
    );
  }

  if (error || !assessment) {
    return <NotFoundPage />;
  }

  if (!encryptedEmail) {
    return (
      <Container size='lg' py='xl'>
        <Title order={2} mb='md'>
          Assessment Detail
        </Title>
        <Paper p='md' withBorder>
          <Text color='red'>
            Missing required parameters. Please use the link provided in your email.
          </Text>
        </Paper>
      </Container>
    );
  }

  const status = getAssessmentAvailabilityStatus(
    assessment.startTime,
    assessment.endTime,
    assessment.isDeleted
  );

  const getCurrentParticipant = () => {
    if (assessment?.typeAssessment === 'Individu') {
      if (activeParticipant) {
        return activeParticipant;
      }
      if (assessment.owner.participants?.length > 0) {
        const firstParticipantId = assessment.owner.participants[0].binusianID;
        if (!activeParticipant) {
          setActiveParticipant(firstParticipantId);
        }
        return firstParticipantId;
      }
      return null;
    }
    return undefined;
  };

  return (
    <Container size='lg' py={{ base: 'md', sm: 'xl' }}>
      <Grid gutter='lg'>
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap='md'>
            <Card
              withBorder
              radius='lg'
              padding='lg'
              style={{ backgroundColor: '#0F172A', color: '#fff' }}
            >
              <Group justify='space-between' align='flex-start'>
                <Stack gap={14}>
                  <Group gap='xs'>
                    {status === 'completed' ? (
                      <Badge color='teal' variant='light' size='lg' radius='sm'>
                        Completed on {dayjs.utc(assessment.endTime).local().format('DD/MM/YYYY')}
                      </Badge>
                    ) : status === 'in progress' ? (
                      <Badge color='yellow' variant='light' size='lg' radius='sm'>
                        In Progress
                      </Badge>
                    ) : status === 'cancelled' ? (
                      <Badge color='red' variant='light' size='lg' radius='sm'>
                        Cancelled
                      </Badge>
                    ) : (
                      <Badge color='blue' variant='light' size='lg' radius='sm'>
                        Pending
                      </Badge>
                    )}
                  </Group>

                  <Title order={2} c='#fff'>
                    {assessment.name}
                  </Title>

                  <Group gap='xs'>
                    <Badge color='gray' bg='gray' variant='solid' size='sm'>
                      {assessment.type}
                    </Badge>
                    <Badge color='gray' bg='gray' variant='solid' size='sm'>
                      {assessment.typeAssessment || 'Team'}
                    </Badge>
                    {assessment.source && (
                      <Badge color='gray' bg='gray' variant='solid' size='sm'>
                        Source: {assessment.source}
                      </Badge>
                    )}
                  </Group>
                </Stack>
              </Group>
            </Card>

            <Card withBorder radius='lg' padding='lg'>
              <Title order={4} mb='md'>
                About this assessment
              </Title>
              <Text style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {assessment.description}
              </Text>

              <Title order={4} mt='xl' mb='md'>
                Notes
              </Title>
              <Text style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {assessment.notes}
              </Text>
            </Card>

            {/* Mobile/Tablet Only: Share Assessment Section */}
            {owner &&
              owner.type === 'Participant' &&
              shareLink != null &&
              availableExternalTypeLabels.length > 0 && (
                <Card withBorder radius='lg' padding='lg' display={{ base: 'block', md: 'none' }}>
                  <Title order={4} mb='md'>
                    Share Assessment
                  </Title>

                  {/* External Role Selection (typeLabel) */}
                  <Select
                    label='Select External Role'
                    placeholder='Choose role for external user'
                    data={availableExternalTypeLabels.map((l) => ({ value: l, label: l }))}
                    value={selectedExternalTypeLabel}
                    onChange={setSelectedExternalTypeLabel}
                    clearable
                    description='Choose which external role view to share. This will lock the role for the external respondent.'
                    mb='md'
                  />

                  {/* Assessment Link */}
                  <Group w='100%' mb='md'>
                    <TextInput
                      value={shareLink}
                      readOnly
                      w='100%'
                      rightSection={
                        <CopyButton value={shareLink} timeout={2000}>
                          {({ copied, copy }) => (
                            <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position='right'>
                              <ActionIcon color={copied ? 'teal' : 'gray'} onClick={copy}>
                                {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </CopyButton>
                      }
                    />
                  </Group>

                  {/* QR Code Section */}
                  <Paper withBorder p='md'>
                    <Stack gap='md' align='center'>
                      <Group justify='space-between' w='100%'>
                        <Text fw={500} size='sm'>
                          QR Code for Quick Access
                        </Text>
                        <Button
                          size='xs'
                          variant='outline'
                          leftSection={<IconDownload size={14} />}
                          onClick={downloadQRCode}
                          loading={isGeneratingQr}
                          disabled={!assessment.linkExternal}
                        >
                          Download QR
                        </Button>
                      </Group>

                      {/* QR Code Display */}
                      {qrCodeDataUrl ? (
                        <Box
                          style={{
                            padding: '12px',
                            backgroundColor: '#ffffff',
                            borderRadius: '8px',
                            border: '2px solid #e9ecef'
                          }}
                        >
                          <img
                            src={qrCodeDataUrl}
                            alt='Assessment QR Code'
                            style={{
                              width: '150px',
                              height: '150px',
                              display: 'block'
                            }}
                          />
                        </Box>
                      ) : isGeneratingQr ? (
                        <Box
                          style={{
                            width: '150px',
                            height: '150px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            border: '2px dashed #dee2e6'
                          }}
                        >
                          <Stack align='center' gap='xs'>
                            <IconQrcode size={32} color='#6c757d' />
                            <Text size='xs' c='dimmed'>
                              Generating...
                            </Text>
                          </Stack>
                        </Box>
                      ) : (
                        <Box
                          style={{
                            width: '150px',
                            height: '150px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            border: '2px dashed #dee2e6',
                            cursor: 'pointer'
                          }}
                          onClick={() =>
                            generateQRCode(assessment.linkExternal).then(setQrCodeDataUrl)
                          }
                        >
                          <Stack align='center' gap='xs'>
                            <IconQrcode size={32} color='#6c757d' />
                            <Text size='xs' c='dimmed'>
                              Click to generate
                            </Text>
                          </Stack>
                        </Box>
                      )}

                      <Text size='xs' c='dimmed' ta='center'>
                        Others can scan this QR code to access the assessment directly
                      </Text>
                    </Stack>
                  </Paper>
                </Card>
              )}

            <Card withBorder radius='lg' padding='lg'>
              <Title order={4} mb='md'>
                Assessment {owner && owner.type === 'Participant' ? 'Evidence' : 'Questions'}
              </Title>

              {owner && owner.name && (
                <Paper p='md' withBorder mb='xl'>
                  <Group gap='md'>
                    <Avatar color='#7B2183' radius='xl' size='md'>
                      {owner.name?.charAt(0)}
                    </Avatar>
                    <div>
                      <Text size='sm' fw={500}>
                        You are participating as:
                      </Text>
                      <Text fw={700}>{owner.name}</Text>
                      <Text size='xs' color='dimmed'>
                        {owner.type} {owner.respondentType ? `(${owner.respondentType})` : ''}
                      </Text>
                    </div>
                  </Group>
                </Paper>
              )}

              {assessment.isAnswer ? (
                <Paper p='lg' withBorder>
                  <Stack align='center' gap='md' mb='xl'>
                    <IconCheck size={48} color='green' />
                    <Title order={3} ta='center'>
                      Assessment Completed
                    </Title>
                    <Text ta='center' color='dimmed'>
                      You have successfully submitted your responses. Thanks for your participation!
                    </Text>
                  </Stack>
                </Paper>
              ) : (
                <>
                  {owner && owner.type === 'Respondent' && (
                    <Alert
                      icon={
                        assessment.typeAssessment === 'Individu' ? (
                          <IconUser size={16} />
                        ) : (
                          <IconUsers size={16} />
                        )
                      }
                      color='blue'
                      mb='lg'
                    >
                      This is a <b>{assessment.typeAssessment || 'Team'}</b> assessment.
                      {assessment.typeAssessment === 'Individu'
                        ? ' You need to evaluate each participant individually.'
                        : ' You need to evaluate the team as a whole.'}
                    </Alert>
                  )}

                  {owner &&
                    owner.type === 'Respondent' &&
                    assessment.typeAssessment === 'Individu' && (
                      <Paper withBorder p='md' mb='xl' data-participant-selector>
                        <Stack gap='md'>
                          <Group>
                            <IconUser size={20} />
                            <Text fw={600} size='lg'>
                              Select Participant to Evaluate
                            </Text>
                          </Group>

                          <Text size='sm' c='dimmed'>
                            As this is an Individual assessment, please select which participant you
                            want to evaluate.
                          </Text>

                          {assessment.owner.participants &&
                          assessment.owner.participants.length > 0 ? (
                            <Select
                              label='Choose Participant'
                              placeholder='Select a participant to evaluate'
                              value={activeParticipant}
                              onChange={(value: any) => {
                                setActiveParticipant(value);
                                setFormError('');
                              }}
                              data={assessment.owner.participants.map(
                                (participant: Participant) => ({
                                  value: participant.binusianId || participant.binusianID,
                                  label: `${participant.binusianName || participant.name || participant.binusianId || participant.binusianID} (${participant.binusianId || participant.binusianID})`
                                })
                              )}
                              searchable
                              clearable
                              size='md'
                              leftSection={<IconUser size={16} />}
                              styles={{
                                input: {
                                  fontSize: '14px'
                                }
                              }}
                            />
                          ) : (
                            <Alert icon={<IconAlertCircle size={16} />} color='orange'>
                              No participants assigned to this assessment.
                            </Alert>
                          )}

                          {/* Show selected participant info */}
                          {activeParticipant && (
                            <Alert icon={<IconCheck size={16} />} color='blue' variant='light'>
                              <Group>
                                <Avatar color='#7B2183' radius='xl' size='sm'>
                                  {(() => {
                                    const participant = assessment.owner.participants?.find(
                                      (p: Participant) =>
                                        (p.binusianId || p.binusianID) === activeParticipant
                                    );
                                    const name =
                                      participant?.binusianName || participant?.name || '';
                                    return name.charAt(0) || '?';
                                  })()}
                                </Avatar>
                                <div>
                                  <Text size='sm' fw={500}>
                                    Currently evaluating:{' '}
                                    {(() => {
                                      const participant = assessment.owner.participants?.find(
                                        (p: Participant) =>
                                          (p.binusianId || p.binusianID) === activeParticipant
                                      );
                                      return (
                                        participant?.binusianName ||
                                        participant?.name ||
                                        activeParticipant
                                      );
                                    })()}
                                  </Text>
                                  <Text size='xs' c='dimmed'>
                                    Answer the questions below for this participant
                                  </Text>
                                </div>
                              </Group>
                            </Alert>
                          )}
                        </Stack>
                      </Paper>
                    )}

                  {owner && owner.type === 'Respondent' && assessment.typeAssessment === 'Team' && (
                    <Paper withBorder p='md' mb='xl' data-team-participants>
                      <Stack gap='md'>
                        <Group>
                          <IconUsers size={20} />
                          <Text fw={600} size='lg'>
                            Team Participants
                          </Text>
                        </Group>
                        <Text size='sm' c='dimmed'>
                          Below is the list of participants included in this Team assessment. You
                          evaluate the team as a whole, not each individual separately.
                        </Text>
                        {assessment.participants && assessment.participants.length > 0 ? (
                          <Stack gap='xs'>
                            {assessment.participants.map((p: Participant) => (
                              <Group key={p.binusianId || p.binusianID} gap='sm'>
                                <Avatar size='sm' radius='xl' color='violet'>
                                  {(p.binusianName || p.name || '?').charAt(0)}
                                </Avatar>
                                <div>
                                  <Text size='sm' fw={500}>
                                    {p.binusianName || p.name || p.binusianId || p.binusianID}
                                  </Text>
                                  {(p.division || p.type) && (
                                    <Text size='xs' c='dimmed'>
                                      {(p.division || p.type) as string}
                                    </Text>
                                  )}
                                </div>
                              </Group>
                            ))}
                          </Stack>
                        ) : (
                          <Alert icon={<IconAlertCircle size={16} />} color='orange'>
                            No participants registered for this team assessment.
                          </Alert>
                        )}
                      </Stack>
                    </Paper>
                  )}

                  {status !== 'in progress' ? (
                    <Alert
                      icon={<IconAlertCircle size={16} />}
                      title='Assessment Not Active'
                      color='blue'
                      mb='lg'
                    >
                      {status === 'pending' ? (
                        <>
                          <Text>This assessment has not started yet.</Text>
                          <Text size='sm' mt='xs' fw={500}>
                            Start Date:{' '}
                            {dayjs.utc(assessment.startTime).local().format('DD/MM/YYYY HH:mm')}
                          </Text>
                        </>
                      ) : status === 'completed' ? (
                        <>
                          <Text>This assessment has already ended.</Text>
                          <Text size='sm' mt='xs' fw={500}>
                            End Date:{' '}
                            {dayjs.utc(assessment.endTime).local().format('DD/MM/YYYY HH:mm')}
                          </Text>
                        </>
                      ) : (
                        <Text>This assessment is not currently available.</Text>
                      )}
                    </Alert>
                  ) : null}

                  {(() => {
                    const allowedModules = getParticipantAllowedModules();
                    // Only show modules that have relevant questions for the current owner
                    const visibleModules = allowedModules.filter((module: Module) => {
                      const ownerRespondentType = owner?.respondentType || '';
                      const moduleQs = module.questions || [];
                      let relevantQuestions: Question[] = [];
                      if (owner?.type === 'Participant') {
                        relevantQuestions = moduleQs.filter(
                          (q: Question) => q.respondentType === 'Self'
                        );
                      } else {
                        relevantQuestions = moduleQs.filter((q: Question) =>
                          owner?.isSelfAssess
                            ? q.respondentType === 'Self' ||
                              q.respondentType === ownerRespondentType
                            : q.respondentType === ownerRespondentType
                        );
                      }
                      return relevantQuestions.length > 0;
                    });
                    const showModuleTabs = visibleModules && visibleModules.length > 0;
                    const isIndividualWithoutParticipant =
                      assessment?.typeAssessment === 'Individu' &&
                      owner?.type === 'Respondent' &&
                      !activeParticipant;

                    return (
                      showModuleTabs && (
                        <ScrollArea>
                          <Tabs
                            value={activeModule}
                            onChange={(value) => {
                              if (isIndividualWithoutParticipant) {
                                setFormError(
                                  'Please select a participant first before choosing a module.'
                                );
                                return;
                              }
                              setActiveModule(value);
                            }}
                            mb='xl'
                          >
                            <Tabs.List>
                              {visibleModules.map((module: Module) => {
                                const getModuleProgress = () => {
                                  const ownerRespondentType = owner?.respondentType || '';
                                  const moduleQs = module.questions || [];

                                  let relevantQuestions: Question[] = [];

                                  if (owner?.type === 'Participant') {
                                    relevantQuestions = moduleQs.filter(
                                      (q: Question) => q.respondentType === 'Self'
                                    );
                                  } else {
                                    relevantQuestions = moduleQs.filter((q: Question) =>
                                      owner?.isSelfAssess
                                        ? q.respondentType === 'Self' ||
                                          q.respondentType === ownerRespondentType
                                        : q.respondentType === ownerRespondentType
                                    );
                                  }

                                  if (relevantQuestions.length === 0) {
                                    return {
                                      completed: 0,
                                      total: 0,
                                      percentage: 100,
                                      status: 'no-questions'
                                    };
                                  }

                                  let completed = 0;
                                  let total = relevantQuestions.length;

                                  // For individual assessments answered by a Respondent,
                                  // only count the currently selected participant
                                  if (
                                    assessment?.typeAssessment === 'Individu' &&
                                    owner?.type === 'Respondent'
                                  ) {
                                    if (activeParticipant) {
                                      relevantQuestions.forEach((question: Question) => {
                                        const isAnswered =
                                          questionCompletionStatus[module.moduleID]?.[
                                            activeParticipant
                                          ]?.[question.trAssessmentQuestionID];
                                        if (isAnswered) completed++;
                                      });
                                    } else {
                                      // No participant selected yet: show 0 completed out of total
                                      completed = 0;
                                    }
                                  } else {
                                    // Team or participant self-assessment counting
                                    relevantQuestions.forEach((question: Question) => {
                                      const isAnswered =
                                        assessment?.typeAssessment === 'Individu' &&
                                        activeParticipant
                                          ? questionCompletionStatus[module.moduleID]?.[
                                              activeParticipant
                                            ]?.[question.trAssessmentQuestionID]
                                          : questionCompletionStatus[module.moduleID]?.['team']?.[
                                              question.trAssessmentQuestionID
                                            ];
                                      if (isAnswered) completed++;
                                    });
                                  }

                                  const percentage =
                                    total > 0 ? Math.round((completed / total) * 100) : 100;

                                  return {
                                    completed,
                                    total,
                                    percentage,
                                    status:
                                      percentage === 100
                                        ? 'complete'
                                        : percentage > 0
                                          ? 'partial'
                                          : 'incomplete'
                                  };
                                };

                                const progress = getModuleProgress();

                                const evidenceProgress =
                                  owner?.type === 'Participant' &&
                                  assessment?.type?.toLowerCase() === 'product'
                                    ? {
                                        hasEvidence:
                                          assessment.moduleEvidence &&
                                          assessment.moduleEvidence[module.moduleID] &&
                                          assessment.moduleEvidence[module.moduleID].length > 0,
                                        status:
                                          assessment.moduleEvidence &&
                                          assessment.moduleEvidence[module.moduleID] &&
                                          assessment.moduleEvidence[module.moduleID].length > 0
                                            ? 'complete'
                                            : 'required'
                                      }
                                    : null;

                                return (
                                  <Tabs.Tab
                                    value={module.moduleID}
                                    key={module.moduleID}
                                    disabled={isIndividualWithoutParticipant}
                                    style={{
                                      opacity: isIndividualWithoutParticipant ? 0.5 : 1,
                                      cursor: isIndividualWithoutParticipant
                                        ? 'not-allowed'
                                        : 'pointer'
                                    }}
                                    rightSection={
                                      <Group gap={4}>
                                        {activeModule === module.moduleID && (
                                          <Tooltip label='Selected (Active)'>
                                            <Badge
                                              color='blue'
                                              size='xs'
                                              variant='filled'
                                              style={{
                                                minWidth: 20,
                                                height: 20,
                                                padding: 0,
                                                fontSize: '10px'
                                              }}
                                            >
                                              S
                                            </Badge>
                                          </Tooltip>
                                        )}
                                        <Tooltip
                                          label={
                                            progress.status === 'no-questions'
                                              ? 'No questions for your role'
                                              : `Questions: ${progress.completed}/${progress.total} completed (${progress.percentage}%)`
                                          }
                                        >
                                          <Badge
                                            color={
                                              assessment.isAnswer
                                                ? 'green'
                                                : progress.status === 'complete'
                                                  ? 'green'
                                                  : progress.status === 'partial'
                                                    ? 'yellow'
                                                    : progress.status === 'no-questions'
                                                      ? 'gray'
                                                      : 'red'
                                            }
                                            size='xs'
                                            variant='filled'
                                            style={{
                                              minWidth: 28,
                                              height: 20,
                                              padding: 0,
                                              fontSize: '10px'
                                            }}
                                          >
                                            {assessment.isAnswer
                                              ? '✓'
                                              : progress.status === 'no-questions'
                                                ? '-'
                                                : `${progress.completed}/${progress.total}`}
                                          </Badge>
                                        </Tooltip>

                                        {evidenceProgress && (
                                          <Tooltip
                                            label={
                                              evidenceProgress.status === 'complete'
                                                ? 'Evidence uploaded'
                                                : 'Evidence required'
                                            }
                                          >
                                            <Badge
                                              color={
                                                evidenceProgress.status === 'complete'
                                                  ? 'green'
                                                  : 'red'
                                              }
                                              size='xs'
                                              variant='filled'
                                              style={{
                                                minWidth: 20,
                                                height: 20,
                                                padding: 0,
                                                fontSize: '10px'
                                              }}
                                            >
                                              E
                                            </Badge>
                                          </Tooltip>
                                        )}
                                      </Group>
                                    }
                                  >
                                    <Stack gap={2} align='flex-start'>
                                      <Text fw={500} size='sm' color='dimmed'>
                                        {module.moduleName || `${module.moduleID}`}
                                      </Text>
                                      {/* Progress bar under module name */}
                                      {progress.status !== 'no-questions' &&
                                        !assessment.isAnswer && (
                                          <div
                                            style={{
                                              width: '100%',
                                              height: '3px',
                                              backgroundColor: '#f1f1f1',
                                              borderRadius: '2px',
                                              overflow: 'hidden'
                                            }}
                                          >
                                            <div
                                              style={{
                                                width: `${progress.percentage}%`,
                                                height: '100%',
                                                backgroundColor:
                                                  progress.status === 'complete'
                                                    ? '#28a745'
                                                    : progress.status === 'partial'
                                                      ? '#ffc107'
                                                      : '#dc3545',
                                                transition: 'width 0.3s ease'
                                              }}
                                            />
                                          </div>
                                        )}
                                    </Stack>
                                  </Tabs.Tab>
                                );
                              })}
                            </Tabs.List>
                          </Tabs>
                        </ScrollArea>
                      )
                    );
                  })()}

                  {owner &&
                    owner.type === 'Respondent' &&
                    assessment?.typeAssessment === 'Individu' &&
                    !activeParticipant && (
                      <Paper
                        withBorder
                        p='xl'
                        style={{
                          backgroundColor: '#fff8e1',
                          borderColor: '#ffc107',
                          textAlign: 'center'
                        }}
                      >
                        <Stack align='center' gap='md'>
                          <IconUser size={48} color='#ffc107' />
                          <Title order={4} c='#f57c00'>
                            Select a Participant to Begin
                          </Title>
                          <Text c='dimmed' size='sm' style={{ maxWidth: 400 }}>
                            This is an Individual assessment. Please select a participant from the
                            dropdown above to start answering questions for that specific person.
                          </Text>
                          <Button
                            variant='outline'
                            color='orange'
                            onClick={() => {
                              // Scroll to participant dropdown
                              const participantSection = document.querySelector(
                                '[data-participant-selector]'
                              );
                              if (participantSection) {
                                participantSection.scrollIntoView({
                                  behavior: 'smooth',
                                  block: 'center'
                                });
                              }
                            }}
                          >
                            Go to Participant Selection
                          </Button>
                        </Stack>
                      </Paper>
                    )}

                  {formError && (
                    <Alert icon={<IconAlertCircle size={16} />} title='Error' color='red' mb='md'>
                      {formError}
                    </Alert>
                  )}

                  {draftSaved && (
                    <Alert icon={<IconCheck size={16} />} title='Draft Saved' color='green' mb='md'>
                      Your progress has been saved locally. You can continue later.
                    </Alert>
                  )}

                  {(!owner || owner.type !== 'Participant') && (
                    <>
                      {(() => {
                        if (!activeModule) return false;
                        const currentModule = assessment.modules.find(
                          (m: Module) => m.moduleID === activeModule
                        );
                        if (!currentModule) return false;
                        const ownerRespondentType = owner?.respondentType || '';
                        const moduleQs = currentModule.questions || [];
                        let hasRelevant = false;
                        if (owner?.type === 'Participant') {
                          hasRelevant = moduleQs.some((q: Question) => q.respondentType === 'Self');
                        } else {
                          hasRelevant = moduleQs.some((q: Question) =>
                            owner?.isSelfAssess
                              ? q.respondentType === 'Self' ||
                                q.respondentType === ownerRespondentType
                              : q.respondentType === ownerRespondentType
                          );
                        }
                        return hasRelevant;
                      })() ? (
                        <Stack gap='xl' mt={'xl'}>
                          <div>
                            {/* get the learning objectives for active module */}
                            {assessment.modules.find((m: Module) => m.moduleID === activeModule)
                              ?.learningObjectives && (
                              <>
                                <Text
                                  style={{
                                    fontWeight: 'bold',
                                    fontSize: '1.2rem'
                                  }}
                                >
                                  Learning Obectives
                                </Text>
                                <div
                                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                  dangerouslySetInnerHTML={{
                                    __html:
                                      assessment.modules.find(
                                        (m: Module) => m.moduleID === activeModule
                                      )?.learningObjectives || ''
                                  }}
                                />
                              </>
                            )}
                          </div>
                          <div ref={questionSectionRef}>
                            <Accordion
                              multiple
                              defaultValue={[
                                'self',
                                'peer',
                                'supervisor',
                                'subordinate',
                                'external'
                              ]}
                            >
                              {(() => {
                                const currentModule = assessment.modules.find(
                                  (m: Module) => m.moduleID === activeModule
                                );
                                if (!currentModule || !currentModule.questions) return null;

                                const groups = getQuestionGroups(currentModule.questions);
                                const filteredGroups: Record<string, Question[]> = {};
                                const ownerRespondentType = owner?.respondentType || '';
                                const existingAnswers = getExistingAnswers();
                                const isReadonly = assessment.isAnswer || status !== 'in progress';

                                // Include Self Assessment questions if applicable
                                if (owner?.isSelfAssess && groups['Self']) {
                                  filteredGroups['Self'] = groups['Self'];
                                }

                                // Include questions based on respondent type
                                if (ownerRespondentType && groups[ownerRespondentType]) {
                                  filteredGroups[ownerRespondentType] = groups[ownerRespondentType];
                                }

                                return Object.entries(filteredGroups).map(
                                  ([groupName, questionList]) => {
                                    // Calculate progress for this group
                                    const getGroupProgress = () => {
                                      if (isReadonly)
                                        return {
                                          completed: questionList.length,
                                          total: questionList.length
                                        };

                                      let completed = 0;
                                      const total = questionList.length;

                                      questionList.forEach((question: Question) => {
                                        const isAnswered =
                                          assessment?.typeAssessment === 'Individu' &&
                                          activeParticipant
                                            ? questionCompletionStatus[activeModule!]?.[
                                                activeParticipant
                                              ]?.[question.trAssessmentQuestionID]
                                            : questionCompletionStatus[activeModule!]?.['team']?.[
                                                question.trAssessmentQuestionID
                                              ];

                                        if (isAnswered) completed++;
                                      });

                                      return { completed, total };
                                    };

                                    const progress = getGroupProgress();
                                    const progressPercentage =
                                      progress.total > 0
                                        ? Math.round((progress.completed / progress.total) * 100)
                                        : 100;

                                    return (
                                      <Accordion.Item
                                        key={groupName}
                                        value={groupName.toLowerCase()}
                                      >
                                        <Accordion.Control>
                                          <Group justify='space-between' w='100%' pr='md'>
                                            <Group gap='sm'>
                                              <Text fw={600} size='lg'>
                                                {groupName} Questions ({questionList.length})
                                              </Text>
                                              {/* Progress indicator */}
                                              <Badge
                                                color={
                                                  isReadonly
                                                    ? 'green'
                                                    : progressPercentage === 100
                                                      ? 'green'
                                                      : progressPercentage > 0
                                                        ? 'yellow'
                                                        : 'red'
                                                }
                                                size='sm'
                                                variant={isReadonly ? 'filled' : 'outline'}
                                              >
                                                {isReadonly
                                                  ? 'Completed'
                                                  : `${progress.completed}/${progress.total}`}
                                              </Badge>
                                            </Group>

                                            {/* Progress bar */}
                                            {!isReadonly && (
                                              <Box style={{ width: '120px' }}>
                                                <div
                                                  style={{
                                                    width: '100%',
                                                    height: '8px',
                                                    backgroundColor: '#f1f1f1',
                                                    borderRadius: '4px',
                                                    overflow: 'hidden',
                                                    marginBottom: '4px'
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      width: `${progressPercentage}%`,
                                                      height: '100%',
                                                      backgroundColor:
                                                        progressPercentage === 100
                                                          ? '#28a745'
                                                          : progressPercentage > 0
                                                            ? '#ffc107'
                                                            : '#dc3545',
                                                      transition: 'width 0.3s ease'
                                                    }}
                                                  />
                                                </div>
                                                <Text size='xs' ta='center' c='dimmed'>
                                                  {progressPercentage}% Complete
                                                </Text>
                                              </Box>
                                            )}
                                          </Group>
                                        </Accordion.Control>
                                        <Accordion.Panel>
                                          <Stack gap='md'>
                                            {questionList.map(
                                              (question: Question, index: number) => (
                                                <Box
                                                  id={`question-${activeModule}-${question.trAssessmentQuestionID}`}
                                                  key={question.trAssessmentQuestionID || index}
                                                  p='md'
                                                  style={{
                                                    backgroundColor: isReadonly
                                                      ? '#f8f9fa'
                                                      : '#f8f9fa',
                                                    borderRadius: '8px',
                                                    border: (() => {
                                                      if (isReadonly) return '2px solid #28a745';

                                                      const isAnswered =
                                                        assessment?.typeAssessment === 'Individu' &&
                                                        activeParticipant
                                                          ? questionCompletionStatus[
                                                              activeModule!
                                                            ]?.[activeParticipant]?.[
                                                              question.trAssessmentQuestionID
                                                            ]
                                                          : questionCompletionStatus[
                                                              activeModule!
                                                            ]?.['team']?.[
                                                              question.trAssessmentQuestionID
                                                            ];

                                                      return isAnswered
                                                        ? '2px solid #28a745'
                                                        : '2px solid #ffa500';
                                                    })(),
                                                    position: 'relative',
                                                    transition: 'all 0.3s ease'
                                                  }}
                                                >
                                                  {/* Question Status Indicator */}
                                                  {!isReadonly && (
                                                    <Group
                                                      style={{
                                                        position: 'absolute',
                                                        top: '12px',
                                                        right: '12px',
                                                        zIndex: 10
                                                      }}
                                                      gap={4}
                                                    >
                                                      <Tooltip
                                                        label={(() => {
                                                          const isAnswered =
                                                            assessment?.typeAssessment ===
                                                              'Individu' && activeParticipant
                                                              ? questionCompletionStatus[
                                                                  activeModule!
                                                                ]?.[activeParticipant]?.[
                                                                  question.trAssessmentQuestionID
                                                                ]
                                                              : questionCompletionStatus[
                                                                  activeModule!
                                                                ]?.['team']?.[
                                                                  question.trAssessmentQuestionID
                                                                ];

                                                          return isAnswered
                                                            ? 'Question answered'
                                                            : 'Question required';
                                                        })()}
                                                      >
                                                        <Badge
                                                          color={(() => {
                                                            const isAnswered =
                                                              assessment?.typeAssessment ===
                                                                'Individu' && activeParticipant
                                                                ? questionCompletionStatus[
                                                                    activeModule!
                                                                  ]?.[activeParticipant]?.[
                                                                    question.trAssessmentQuestionID
                                                                  ]
                                                                : questionCompletionStatus[
                                                                    activeModule!
                                                                  ]?.['team']?.[
                                                                    question.trAssessmentQuestionID
                                                                  ];

                                                            return isAnswered ? 'green' : 'orange';
                                                          })()}
                                                          size='sm'
                                                          variant='filled'
                                                          style={{ cursor: 'help' }}
                                                        >
                                                          {(() => {
                                                            const isAnswered =
                                                              assessment?.typeAssessment ===
                                                                'Individu' && activeParticipant
                                                                ? questionCompletionStatus[
                                                                    activeModule!
                                                                  ]?.[activeParticipant]?.[
                                                                    question.trAssessmentQuestionID
                                                                  ]
                                                                : questionCompletionStatus[
                                                                    activeModule!
                                                                  ]?.['team']?.[
                                                                    question.trAssessmentQuestionID
                                                                  ];

                                                            return isAnswered ? '✓' : '!';
                                                          })()}
                                                        </Badge>
                                                      </Tooltip>
                                                    </Group>
                                                  )}

                                                  <Group gap={8} align='flex-start'>
                                                    <Text fw={500} size='md' color='#333'>
                                                      {index + 1}.
                                                    </Text>
                                                    <Stack
                                                      gap={4}
                                                      style={{ flex: 1, paddingRight: '60px' }}
                                                    >
                                                      <Group
                                                        justify='space-between'
                                                        align='flex-start'
                                                      >
                                                        <Text fw={600} size='md' color='#333'>
                                                          {question.title}
                                                        </Text>
                                                        {!isReadonly && (
                                                          <Text
                                                            size='xs'
                                                            c={(() => {
                                                              const isAnswered =
                                                                assessment?.typeAssessment ===
                                                                  'Individu' && activeParticipant
                                                                  ? questionCompletionStatus[
                                                                      activeModule!
                                                                    ]?.[activeParticipant]?.[
                                                                      question
                                                                        .trAssessmentQuestionID
                                                                    ]
                                                                  : questionCompletionStatus[
                                                                      activeModule!
                                                                    ]?.['team']?.[
                                                                      question
                                                                        .trAssessmentQuestionID
                                                                    ];

                                                              return isAnswered
                                                                ? 'green'
                                                                : 'orange';
                                                            })()}
                                                            fw={500}
                                                          >
                                                            {(() => {
                                                              const isAnswered =
                                                                assessment?.typeAssessment ===
                                                                  'Individu' && activeParticipant
                                                                  ? questionCompletionStatus[
                                                                      activeModule!
                                                                    ]?.[activeParticipant]?.[
                                                                      question
                                                                        .trAssessmentQuestionID
                                                                    ]
                                                                  : questionCompletionStatus[
                                                                      activeModule!
                                                                    ]?.['team']?.[
                                                                      question
                                                                        .trAssessmentQuestionID
                                                                    ];

                                                              return isAnswered
                                                                ? 'Completed'
                                                                : 'Required';
                                                            })()}
                                                          </Text>
                                                        )}
                                                      </Group>

                                                      {question.description && (
                                                        <Text size='sm' color='dimmed' mt={6}>
                                                          {question.description}
                                                        </Text>
                                                      )}

                                                      <Box mt='md'>
                                                        {question.type === 'Range' &&
                                                          question.rangeItems && (
                                                            <Radio.Group
                                                              value={
                                                                isReadonly
                                                                  ? existingAnswers[
                                                                      question
                                                                        .trAssessmentQuestionID
                                                                    ] || ''
                                                                  : assessment.typeAssessment ===
                                                                        'Individu' &&
                                                                      activeParticipant
                                                                    ? responses[activeModule!]?.[
                                                                        activeParticipant
                                                                      ]?.[
                                                                        question
                                                                          .trAssessmentQuestionID
                                                                      ] || ''
                                                                    : responses[activeModule!]
                                                                        ?.team?.[
                                                                        question
                                                                          .trAssessmentQuestionID
                                                                      ] || ''
                                                              }
                                                              onChange={
                                                                isReadonly
                                                                  ? undefined
                                                                  : (value) => {
                                                                      if (activeModule) {
                                                                        handleResponseChange(
                                                                          activeModule,
                                                                          question.trAssessmentQuestionID,
                                                                          value,
                                                                          getCurrentParticipant()
                                                                        );
                                                                      }
                                                                    }
                                                              }
                                                            >
                                                              <Stack gap='xs'>
                                                                {question.rangeItems
                                                                  .slice()
                                                                  .sort(
                                                                    (a, b) =>
                                                                      a.sequence - b.sequence
                                                                  )
                                                                  .map((item) => (
                                                                    <Radio
                                                                      key={
                                                                        item.trAssessmentQuestionRangeItemID
                                                                      }
                                                                      value={item.value}
                                                                      label={`${item.label} (${item.value})`}
                                                                      disabled={isReadonly}
                                                                      styles={
                                                                        isReadonly
                                                                          ? {
                                                                              radio: {
                                                                                cursor:
                                                                                  'not-allowed'
                                                                              },
                                                                              label: {
                                                                                cursor:
                                                                                  'not-allowed',
                                                                                opacity: 0.7
                                                                              }
                                                                            }
                                                                          : undefined
                                                                      }
                                                                    />
                                                                  ))}
                                                              </Stack>
                                                            </Radio.Group>
                                                          )}

                                                        {question.type === 'Free-text' && (
                                                          <Textarea
                                                            placeholder={
                                                              isReadonly
                                                                ? 'No answer provided'
                                                                : 'Enter your response...'
                                                            }
                                                            minRows={3}
                                                            readOnly={isReadonly}
                                                            value={(() => {
                                                              if (isReadonly) {
                                                                return (
                                                                  existingAnswers[
                                                                    question.trAssessmentQuestionID
                                                                  ] || ''
                                                                );
                                                              }
                                                              if (
                                                                assessment.typeAssessment ===
                                                                  'Individu' &&
                                                                activeParticipant
                                                              ) {
                                                                return (
                                                                  responses[activeModule!]?.[
                                                                    activeParticipant
                                                                  ]?.[
                                                                    question.trAssessmentQuestionID
                                                                  ] || ''
                                                                );
                                                              }
                                                              return (
                                                                responses[activeModule!]?.team?.[
                                                                  question.trAssessmentQuestionID
                                                                ] || ''
                                                              );
                                                            })()}
                                                            onChange={
                                                              isReadonly
                                                                ? undefined
                                                                : (e) =>
                                                                    handleResponseChange(
                                                                      activeModule!,
                                                                      question.trAssessmentQuestionID,
                                                                      e.target.value,
                                                                      getCurrentParticipant()
                                                                    )
                                                            }
                                                            styles={
                                                              isReadonly
                                                                ? {
                                                                    input: {
                                                                      backgroundColor: '#e9ecef',
                                                                      cursor: 'not-allowed',
                                                                      color: '#495057'
                                                                    }
                                                                  }
                                                                : undefined
                                                            }
                                                          />
                                                        )}

                                                        {isReadonly &&
                                                          existingAnswers[
                                                            question.trAssessmentQuestionID
                                                          ] && (
                                                            <Alert color='green' mt='sm'>
                                                              <Text size='sm'>
                                                                <strong>Your Answer:</strong>{' '}
                                                                {question.type === 'Range'
                                                                  ? `${existingAnswers[question.trAssessmentQuestionID]} - ${
                                                                      question.rangeItems?.find(
                                                                        (item) =>
                                                                          item.value ===
                                                                          existingAnswers[
                                                                            question
                                                                              .trAssessmentQuestionID
                                                                          ]
                                                                      )?.label || 'Unknown'
                                                                    }`
                                                                  : existingAnswers[
                                                                      question
                                                                        .trAssessmentQuestionID
                                                                    ]}
                                                              </Text>
                                                            </Alert>
                                                          )}
                                                      </Box>
                                                    </Stack>
                                                  </Group>
                                                </Box>
                                              )
                                            )}
                                          </Stack>
                                        </Accordion.Panel>
                                      </Accordion.Item>
                                    );
                                  }
                                );
                              })()}
                            </Accordion>
                          </div>
                        </Stack>
                      ) : (
                        <Text ta='center' color='dimmed'>
                          No questions available for this module.
                        </Text>
                      )}
                    </>
                  )}

                  {owner && owner.type === 'Participant' && activeModule && (
                    <Box mt='sm'>
                      <Group justify='space-between' align='center' mb={15}>
                        <Title fw={600} size='lg'>
                          Self Assessment
                        </Title>
                        {(() => {
                          const currentModule = assessment.modules.find(
                            (m: Module) => m.moduleID === activeModule
                          );
                          if (!currentModule || !currentModule.questions) return null;

                          const selfQuestions = currentModule.questions.filter(
                            (q: Question) => q.respondentType === 'Self'
                          );

                          if (selfQuestions.length === 0) return null;

                          // FIX: Use questionCompletionStatus instead of direct responses check
                          const completedSelfQuestions = selfQuestions.filter(
                            (q: Question) =>
                              questionCompletionStatus[activeModule]?.['team']?.[
                                q.trAssessmentQuestionID
                              ]
                          ).length;

                          const progressPercentage = Math.round(
                            (completedSelfQuestions / selfQuestions.length) * 100
                          );

                          return (
                            <Group gap='sm'>
                              <Badge
                                color={
                                  progressPercentage === 100
                                    ? 'green'
                                    : progressPercentage > 0
                                      ? 'yellow'
                                      : 'red'
                                }
                                size='md'
                                variant='outline'
                              >
                                {completedSelfQuestions}/{selfQuestions.length} Complete
                              </Badge>
                              <Box style={{ width: '150px' }}>
                                <div
                                  style={{
                                    width: '100%',
                                    height: '8px',
                                    backgroundColor: '#f1f1f1',
                                    borderRadius: '4px',
                                    overflow: 'hidden'
                                  }}
                                >
                                  <div
                                    style={{
                                      width: `${progressPercentage}%`,
                                      height: '100%',
                                      backgroundColor:
                                        progressPercentage === 100
                                          ? '#28a745'
                                          : progressPercentage > 0
                                            ? '#ffc107'
                                            : '#dc3545',
                                      transition: 'width 0.3s ease'
                                    }}
                                  />
                                </div>
                                <Text size='xs' ta='center' mt={2} c='dimmed'>
                                  {progressPercentage}%
                                </Text>
                              </Box>
                            </Group>
                          );
                        })()}
                      </Group>

                      {activeModule && (
                        <div ref={questionSectionRef}>
                          <Stack gap='xl'>
                            {(() => {
                              const currentModule = assessment.modules.find(
                                (m: Module) => m.moduleID === activeModule
                              );
                              if (!currentModule || !currentModule.questions) return null;

                              const selfQuestions = currentModule.questions.filter(
                                (q: Question) => q.respondentType === 'Self'
                              );

                              if (selfQuestions.length === 0) {
                                return (
                                  <Paper p='xl' withBorder style={{ textAlign: 'center' }}>
                                    <Stack align='center' gap='md'>
                                      <IconCheck size={48} color='#28a745' />
                                      <Text fw={600} size='lg'>
                                        No Self Assessment Required
                                      </Text>
                                      <Text color='dimmed'>
                                        This module doesn't have self-assessment questions.
                                      </Text>
                                    </Stack>
                                  </Paper>
                                );
                              }

                              return (
                                <Stack gap='md'>
                                  {selfQuestions.map((question: Question, index: number) => (
                                    <Box
                                      id={`question-${activeModule}-${question.trAssessmentQuestionID}`}
                                      key={question.trAssessmentQuestionID || index}
                                      p='md'
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '8px',
                                        border: (() => {
                                          const isAnswered =
                                            questionCompletionStatus[activeModule]?.['team']?.[
                                              question.trAssessmentQuestionID
                                            ];
                                          return isAnswered
                                            ? '2px solid #28a745'
                                            : '2px solid #ffa500';
                                        })(),
                                        position: 'relative',
                                        transition: 'all 0.3s ease'
                                      }}
                                    >
                                      {/* Self Question Status Indicator */}
                                      <Group
                                        style={{
                                          position: 'absolute',
                                          top: '12px',
                                          right: '12px',
                                          zIndex: 10
                                        }}
                                        gap={4}
                                      >
                                        <Tooltip
                                          label={(() => {
                                            const isAnswered =
                                              questionCompletionStatus[activeModule]?.['team']?.[
                                                question.trAssessmentQuestionID
                                              ];
                                            return isAnswered
                                              ? 'Self question answered'
                                              : 'Self question required';
                                          })()}
                                        >
                                          <Badge
                                            color={(() => {
                                              const isAnswered =
                                                questionCompletionStatus[activeModule]?.['team']?.[
                                                  question.trAssessmentQuestionID
                                                ];
                                              return isAnswered ? 'green' : 'orange';
                                            })()}
                                            size='sm'
                                            variant='filled'
                                            style={{ cursor: 'help' }}
                                          >
                                            S
                                            {(() => {
                                              const isAnswered =
                                                questionCompletionStatus[activeModule]?.['team']?.[
                                                  question.trAssessmentQuestionID
                                                ];
                                              return isAnswered ? '✓' : '!';
                                            })()}
                                          </Badge>
                                        </Tooltip>
                                      </Group>

                                      <Group gap={8} align='flex-start'>
                                        <Text fw={500} size='md' color='#333'>
                                          {index + 1}.
                                        </Text>
                                        <Stack gap={4} style={{ flex: 1, paddingRight: '60px' }}>
                                          <Group justify='space-between' align='flex-start'>
                                            <Text
                                              fw={600}
                                              size='md'
                                              color='#333'
                                              style={{
                                                whiteSpace: 'pre-wrap',
                                                overflowWrap: 'anywhere',
                                                lineHeight: 1.6
                                              }}
                                            >
                                              {question.title}
                                            </Text>
                                          </Group>

                                          {question.description && (
                                            <Text size='sm' color='dimmed' mt={6}>
                                              {question.description}
                                            </Text>
                                          )}

                                          {status === 'in progress' && (
                                            <Box mt='md'>
                                              {question.type === 'Range' && question.rangeItems && (
                                                <Radio.Group
                                                  value={
                                                    responses[activeModule]?.team?.[
                                                      question.trAssessmentQuestionID
                                                    ] || ''
                                                  }
                                                  onChange={(value) =>
                                                    handleResponseChange(
                                                      activeModule,
                                                      question.trAssessmentQuestionID,
                                                      value
                                                    )
                                                  }
                                                >
                                                  <Stack gap='xs'>
                                                    {question.rangeItems
                                                      .slice()
                                                      .sort((a, b) => a.sequence - b.sequence)
                                                      .map((item) => (
                                                        <Radio
                                                          key={item.trAssessmentQuestionRangeItemID}
                                                          value={item.value}
                                                          label={`${item.label} (${item.value})`}
                                                        />
                                                      ))}
                                                  </Stack>
                                                </Radio.Group>
                                              )}

                                              {question.type === 'Free-text' && (
                                                <Textarea
                                                  placeholder='Enter your self-assessment response...'
                                                  minRows={3}
                                                  value={
                                                    responses[activeModule]?.team?.[
                                                      question.trAssessmentQuestionID
                                                    ] || ''
                                                  }
                                                  onChange={(e) =>
                                                    handleResponseChange(
                                                      activeModule,
                                                      question.trAssessmentQuestionID,
                                                      e.target.value
                                                    )
                                                  }
                                                />
                                              )}
                                            </Box>
                                          )}
                                        </Stack>
                                      </Group>
                                    </Box>
                                  ))}
                                </Stack>
                              );
                            })()}
                          </Stack>
                        </div>
                      )}

                      {assessment.type?.toLowerCase() === 'product' && (
                        <Box mt='xl'>
                          <Title order={5} mb='md'>
                            Evidence
                          </Title>
                          {!(
                            assessment.moduleEvidence &&
                            assessment.moduleEvidence[activeModule] &&
                            assessment.moduleEvidence[activeModule].length > 0
                          ) &&
                            !(
                              moduleEvidenceFiles[activeModule] &&
                              moduleEvidenceFiles[activeModule].length > 0
                            ) && (
                              <Alert
                                icon={<IconAlertCircle size={16} />}
                                title='Evidence Required'
                                color='red'
                                mb='md'
                              >
                                This is a product assessment. You must submit at least one evidence
                                file for this module.
                              </Alert>
                            )}
                          <Box mb='lg'>
                            {assessment.moduleEvidence &&
                              assessment.moduleEvidence[activeModule] &&
                              assessment.moduleEvidence[activeModule].length > 0 && (
                                <Stack gap='xs' mb='md'>
                                  <Text fw={500}>Submitted evidence:</Text>
                                  <List spacing='xs'>
                                    {assessment.moduleEvidence[activeModule].map(
                                      (evidence: Evidence, index: number) => (
                                        <List.Item
                                          key={index}
                                          icon={getFileIcon(evidence.fileName)}
                                        >
                                          <Group justify='space-between' w='100%'>
                                            <Text size='sm'>{evidence.fileName}</Text>
                                            <Button
                                              variant='subtle'
                                              size='xs'
                                              leftSection={<IconDownload size={16} />}
                                              component='a'
                                              href={evidence.evidenceUrl}
                                              target='_blank'
                                              download
                                            >
                                              Download
                                            </Button>
                                          </Group>
                                        </List.Item>
                                      )
                                    )}
                                  </List>
                                </Stack>
                              )}

                            {status === 'in progress' && (
                              <>
                                <Text fw={500} mb='xs'>
                                  Upload evidence:
                                </Text>
                                <Box
                                  style={(theme) => ({
                                    border: '2px dashed #7B2183',
                                    borderRadius: theme.radius.md,
                                    padding: theme.spacing.xl,
                                    textAlign: 'center',
                                    backgroundColor: '#faf5ff'
                                  })}
                                >
                                  <input
                                    type='file'
                                    id={`evidence-upload-${activeModule}`}
                                    onChange={(e) => handleModuleFileSelect(activeModule, e)}
                                    style={{ display: 'none' }}
                                    accept='.jpg,.jpeg,.png,.pdf,.doc,.docx,.ppt,.pptx'
                                  />
                                  <Button
                                    variant='outline'
                                    color='#7B2183'
                                    component='label'
                                    htmlFor={`evidence-upload-${activeModule}`}
                                    loading={uploadingModule === activeModule}
                                  >
                                    Browse Files
                                  </Button>
                                  <Text size='sm' color='dimmed' mt={8}>
                                    Select files from your computer
                                  </Text>
                                  <Text size='xs' color='red' mt={4} fw={500}>
                                    Max file size: 20 MB per file
                                  </Text>
                                </Box>

                                {moduleEvidenceFiles[activeModule] &&
                                  moduleEvidenceFiles[activeModule].length > 0 && (
                                    <Stack gap='xs' mt='md'>
                                      <Alert
                                        color='blue'
                                        icon={<IconAlertCircle size={16} />}
                                        title='Files Selected - Upload Required'
                                      >
                                        <Text size='sm' mb='xs'>
                                          You have selected{' '}
                                          {moduleEvidenceFiles[activeModule].length} file(s). Please
                                          click "Upload Evidence" to save them to the system.
                                        </Text>
                                      </Alert>

                                      <Text fw={500}>Selected files:</Text>
                                      <List spacing='xs'>
                                        {moduleEvidenceFiles[activeModule].map((file, index) => (
                                          <List.Item key={index} icon={getFileIcon(file.name)}>
                                            <Group justify='space-between' w='100%'>
                                              <div>
                                                <Text size='sm'>{file.name}</Text>
                                                <Text size='xs' color='dimmed'>
                                                  {file.size / 1024 < 1000
                                                    ? `${(file.size / 1024).toFixed(2)} KB`
                                                    : `${(file.size / (1024 * 1024)).toFixed(2)} MB`}
                                                </Text>
                                              </div>
                                              <Button
                                                variant='subtle'
                                                color='red'
                                                size='xs'
                                                leftSection={<IconTrash size={16} />}
                                                onClick={() =>
                                                  handleRemoveModuleFile(activeModule, index)
                                                }
                                              >
                                                Remove
                                              </Button>
                                            </Group>
                                          </List.Item>
                                        ))}
                                      </List>

                                      <Group justify='flex-end' mt='md'>
                                        <Button
                                          color='#7B2183'
                                          onClick={() => handleSubmitModuleEvidence(activeModule)}
                                          loading={uploadingModule === activeModule}
                                          leftSection={<IconSend size={16} />}
                                        >
                                          Upload Evidence
                                        </Button>
                                      </Group>
                                    </Stack>
                                  )}
                              </>
                            )}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}

                  {status === 'in progress' &&
                    (() => {
                      if (owner?.type === 'Participant') {
                        const hasParticipantQuestions = assessment?.modules?.some((m: Module) =>
                          (m.questions || []).some((q: Question) => q.respondentType === 'Self')
                        );
                        if (!hasParticipantQuestions) {
                          return (
                            <Paper withBorder p='xl' mt='xl' style={{ textAlign: 'center' }}>
                              <Stack align='center' gap='md'>
                                <IconCheck size={48} color='#28a745' />
                                <Title order={4} c='dimmed'>
                                  No Questions Assigned
                                </Title>
                                <Text size='sm' c='dimmed' style={{ maxWidth: 400 }}>
                                  There are no self-assessment questions assigned to you for this
                                  assessment. No action is required.
                                </Text>
                              </Stack>
                            </Paper>
                          );
                        }
                      }

                      return (
                        <Group justify='flex-end' mt='xl'>
                          <div style={{ width: '100%' }}>
                            {!formSubmitted && !isSubmitting && <ValidationFeedback />}
                            {formError && !formSubmitted && !isSubmitting && (
                              <Alert
                                icon={<IconAlertCircle size={16} />}
                                title='Please Complete Required Actions'
                                color='red'
                                mb='md'
                              >
                                {formError}
                              </Alert>
                            )}
                            {/* Save draft notification */}
                            {draftSaved && !formSubmitted && (
                              <Alert
                                icon={<IconCheck size={16} />}
                                title='Draft Saved'
                                color='green'
                                mb='md'
                              >
                                Your progress has been saved locally. You can continue later.
                              </Alert>
                            )}
                            {/* Show submission status */}
                            {isSubmitting && (
                              <Alert
                                icon={<IconSend size={16} />}
                                title='Submitting Assessment...'
                                color='blue'
                                mb='md'
                              >
                                Please wait while we save your assessment. Do not close this page.
                              </Alert>
                            )}
                          </div>

                          <Group justify='flex-end' w='100%'>
                            <Button
                              variant='outline'
                              color='#7B2183'
                              leftSection={
                                draftSaved ? (
                                  <IconCheck size={16} />
                                ) : (
                                  <IconDeviceFloppy size={16} />
                                )
                              }
                              onClick={saveDraft}
                              disabled={formSubmitted || isSubmitting}
                            >
                              {draftSaved ? 'Draft Saved' : 'Save Draft'}
                            </Button>

                            {status === 'in progress' && (
                              <Button
                                color='#7B2183'
                                leftSection={<IconSend size={16} />}
                                onClick={handleSubmitOrNavigate}
                                loading={isSubmitting || submitMutation.isPending}
                                disabled={formSubmitted || isSubmitting || submitMutation.isPending}
                              >
                                {isSubmitting || submitMutation.isPending
                                  ? 'Submitting...'
                                  : 'Submit Assessment'}
                              </Button>
                            )}
                          </Group>
                        </Group>
                      );
                    })()}
                </>
              )}
            </Card>
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <Card withBorder radius='lg' padding='lg'>
            <Stack gap='md'>
              <Flex gap='xs' align='center'>
                <IconClock size={20} />
                <Title order={4}>Timeline</Title>
              </Flex>

              <Group gap='xs' wrap='nowrap'>
                <IconCalendar size={20} />
                <Stack gap={0}>
                  <Text size='sm' fw={500}>
                    Start Date
                  </Text>
                  <Text size='sm'>
                    {dayjs.utc(assessment.startTime).local().format('DD/MM/YYYY HH:mm')}
                  </Text>
                </Stack>
              </Group>

              <Group gap='xs' wrap='nowrap'>
                <IconCalendar size={20} />
                <Stack gap={0}>
                  <Text size='sm' fw={500}>
                    End Date
                  </Text>
                  <Text size='sm'>
                    {dayjs.utc(assessment.endTime).local().format('DD/MM/YYYY HH:mm')}
                  </Text>
                </Stack>
              </Group>

              <div style={{ margin: '12px 0', height: '1px', backgroundColor: '#e0e0e0' }} />

              <Flex gap='xs' align='center'>
                {assessment.typeAssessment === 'Individu' ? (
                  <IconUser size={20} />
                ) : (
                  <IconUsers size={20} />
                )}
                <Title order={4}>Assessment Type</Title>
              </Flex>

              <Paper p='sm' withBorder>
                <Group>
                  <Badge
                    color={assessment.typeAssessment === 'Individu' ? 'blue' : 'violet'}
                    size='lg'
                  >
                    {assessment.typeAssessment === 'Individu' ? 'Individual' : 'Team'}
                  </Badge>
                  <Text size='sm'>
                    {assessment.typeAssessment === 'Individu'
                      ? 'Evaluate each participant individually'
                      : 'Evaluate the team as a whole'}
                  </Text>
                </Group>
              </Paper>

              {owner &&
                owner.type === 'Participant' &&
                shareLink != null &&
                availableExternalTypeLabels.length > 0 && (
                  <Box display={{ base: 'none', md: 'block' }}>
                    <Divider mt={20} />
                    <Title order={4} mt='md'>
                      Share Assessment
                    </Title>

                    {/* External Role Selection (typeLabel) */}
                    <Select
                      label='Select External Role'
                      placeholder='Choose role for external user'
                      data={availableExternalTypeLabels.map((l) => ({ value: l, label: l }))}
                      value={selectedExternalTypeLabel}
                      onChange={setSelectedExternalTypeLabel}
                      clearable
                      description='Choose which external role view to share. This will lock the role for the external respondent.'
                      mb='md'
                    />

                    {/* Assessment Link */}
                    <Group w='100%'>
                      <TextInput
                        value={shareLink}
                        readOnly
                        w='100%'
                        rightSection={
                          <CopyButton value={shareLink} timeout={2000}>
                            {({ copied, copy }) => (
                              <Tooltip
                                label={copied ? 'Copied' : 'Copy'}
                                withArrow
                                position='right'
                              >
                                <ActionIcon color={copied ? 'teal' : 'gray'} onClick={copy}>
                                  {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </CopyButton>
                        }
                      />
                    </Group>

                    {/* QR Code Section */}
                    <Paper withBorder p='md' mt='md'>
                      <Stack gap='md' align='center'>
                        <Group justify='space-between' w='100%'>
                          <Text fw={500} size='sm'>
                            QR Code for Quick Access
                          </Text>
                          <Button
                            size='xs'
                            variant='outline'
                            leftSection={<IconDownload size={14} />}
                            onClick={downloadQRCode}
                            loading={isGeneratingQr}
                            disabled={!assessment.linkExternal}
                          >
                            Download QR
                          </Button>
                        </Group>

                        {/* QR Code Display */}
                        {qrCodeDataUrl ? (
                          <Box
                            style={{
                              padding: '12px',
                              backgroundColor: '#ffffff',
                              borderRadius: '8px',
                              border: '2px solid #e9ecef'
                            }}
                          >
                            <img
                              src={qrCodeDataUrl}
                              alt='Assessment QR Code'
                              style={{
                                width: '150px',
                                height: '150px',
                                display: 'block'
                              }}
                            />
                          </Box>
                        ) : isGeneratingQr ? (
                          <Box
                            style={{
                              width: '150px',
                              height: '150px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '8px',
                              border: '2px dashed #dee2e6'
                            }}
                          >
                            <Stack align='center' gap='xs'>
                              <IconQrcode size={32} color='#6c757d' />
                              <Text size='xs' c='dimmed'>
                                Generating...
                              </Text>
                            </Stack>
                          </Box>
                        ) : (
                          <Box
                            style={{
                              width: '150px',
                              height: '150px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '8px',
                              border: '2px dashed #dee2e6',
                              cursor: 'pointer'
                            }}
                            onClick={() =>
                              generateQRCode(assessment.linkExternal).then(setQrCodeDataUrl)
                            }
                          >
                            <Stack align='center' gap='xs'>
                              <IconQrcode size={32} color='#6c757d' />
                              <Text size='xs' c='dimmed'>
                                Click to generate
                              </Text>
                            </Stack>
                          </Box>
                        )}

                        <Text size='xs' c='dimmed' ta='center'>
                          Others can scan this QR code to access the assessment directly
                        </Text>
                      </Stack>
                    </Paper>
                  </Box>
                )}

              {assessment.userProgress && (
                <>
                  <div style={{ margin: '12px 0', height: '1px', backgroundColor: '#e0e0e0' }} />
                  <Title order={4}>Your Progress</Title>
                  <Stack gap='xs'>
                    <Group justify='space-between'>
                      <Text size='sm'>Completion:</Text>
                      <Text size='sm' fw={500}>
                        {assessment.userProgress?.isCompleted
                          ? '100%'
                          : `${getDetailedProgressData.progressPercentage}%`}
                      </Text>
                    </Group>
                    <div
                      style={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: '#f1f1f1',
                        borderRadius: '4px'
                      }}
                    >
                      <div
                        style={{
                          width: assessment.userProgress?.isCompleted
                            ? '100%'
                            : `${getDetailedProgressData.progressPercentage}%`,
                          height: '100%',
                          backgroundColor: '#7B2183',
                          borderRadius: '4px'
                        }}
                      />
                    </div>
                    {assessment.userProgress?.isCompleted && (
                      <Text size='xs' color='dimmed'>
                        Completed on{' '}
                        {dayjs
                          .utc(assessment.userProgress.completionDate)
                          .local()
                          .format('DD/MM/YYYY HH:mm')}
                      </Text>
                    )}
                  </Stack>
                </>
              )}
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>
    </Container>
  );
};

export default PublicAssessmentDetailPage;
